/*
==========================================
STAMP UI ENGINE  v4.8.3
文件: assets/stamp-ui.js

v4.8.0 改动：
- [REFACTOR] Crop 逻辑彻底拆分到 stamp-crop.js v2.0.0
- handlePhotoUpload：调用 StampCrop.open() 并传入 onConfirm 回调
- confirmCrop 重命名为 _afterCropConfirm(dataUrl)，接收已裁剪的 dataUrl
- setupListeners：移除 crop 事件绑定（已由 StampCrop 内部处理）
- cropState / activeCropShape / _cropContainerW/H 变量移至 stamp-crop.js
- openCropModal / closeCropModal / handleCropZoom / handleCropDragStart
  handleCropDrag / updateCropTransform / cropToCanvas / applyCropShape 移至 stamp-crop.js
- CROP_SHAPES 由 StampCrop.CROP_SHAPES 提供，本文件保留本地引用

v4.6.9 改动：
- [FIX] 所有布局统一先 CROP 原图，再处理风格（body-template / curved-text 也走 crop modal）
==========================================
*/
window.StampUI = (function () {
  'use strict';

  const Core = window.StampCore;

  let _config, _variants, _creds;
  let selectedSize='', selectedInk='', selectedPhotoStyle='line-art';
  let customerEmailValue='', originalUploadedImage=null;
  let curBorderStyle='none', curBorderInset=10, curBorderWidth=2;
  let _cropShapeKey = 'circle';   // ★ v4.8.0: 只保留结果 key，其余状态在 stamp-crop.js

  let _previewDragOffsetX = 0, _previewDragOffsetY = 0;
  let _previewDragActive = false, _previewDragStartX = 0, _previewDragStartY = 0;
  let _previewDragStartOX = 0, _previewDragStartOY = 0;
  let _illusDragOffsetX = 0, _illusDragOffsetY = 0;
  let _illusDragActive = false, _illusDragMoved = false;
  let _illusDragStartX = 0, _illusDragStartY = 0, _illusDragStartOX = 0, _illusDragStartOY = 0;

  let _textOffsets = {};
  let _textDragActive = false, _textDragLineIndex = -1;
  let _textDragStartX = 0, _textDragStartY = 0;
  let _textDragStartOX = 0, _textDragStartOY = 0;
  let _textDragMoved = false;

  let _touchStartX = 0, _touchStartY = 0, _touchStartTime = 0;

  // ★ FIX: DEFAULT_PHOTO_SIZE 由 config.illustration.defaultSize 决定，回退 38
  let DEFAULT_PHOTO_SIZE = 38;

  /* ── v4.8.0: CROP_SHAPES 从 stamp-crop.js 获取，兜底本地定义 ── */
  function _getCropShapes() {
    return (window.StampCrop && StampCrop.CROP_SHAPES) || {
      circle:    { w:1,   h:1,   radius:'50%',  label:'⬤ Circle'    },
      square:    { w:1,   h:1,   radius:'12px', label:'■ Square'    },
      h_oval:    { w:1.4, h:1,   radius:'50%',  label:'⬭ H-Oval'   },
      v_oval:    { w:1,   h:1.4, radius:'50%',  label:'⬬ V-Oval'   },
      landscape: { w:1.4, h:1,   radius:'12px', label:'▬ Landscape' },
      portrait:  { w:1,   h:1.4, radius:'12px', label:'▮ Portrait'  },
    };
  }

  function _doResetSize() {
    const sl = dom('prodPhotoSizeSlider');
    if (!sl) return;
    sl.value = DEFAULT_PHOTO_SIZE;
    const fill = ((DEFAULT_PHOTO_SIZE - +sl.min) / (+sl.max - +sl.min) * 100).toFixed(1) + '%';
    sl.style.setProperty('--fill', fill);
    const valEl = dom('prodPhotoSizeVal'); if (valEl) valEl.textContent = DEFAULT_PHOTO_SIZE + '%';
    const slNew = dom('prodPhotoSizeSliderNew');
    if (slNew) {
      slNew.value = DEFAULT_PHOTO_SIZE;
      const fillNew = ((DEFAULT_PHOTO_SIZE - +slNew.min) / (+slNew.max - +slNew.min) * 100).toFixed(1) + '%';
      slNew.style.setProperty('--fill', fillNew);
      const valNew = dom('prodPhotoSizeValNew'); if (valNew) valNew.textContent = DEFAULT_PHOTO_SIZE + '%';
    }
    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      c.querySelectorAll('.prod-illustration-center,.prod-illustration-left,.prod-illustration-top,.prod-photo-preview,.prod-photo-preview-area')
        .forEach(el => { delete el.dataset.baseSizePx; });
    });
    updatePhotoCenterSize(DEFAULT_PHOTO_SIZE);
  }

  function _doResetPosition() {
    _previewDragOffsetX = 0;
    _previewDragOffsetY = 0;
    applyPhotoPosition();
    const isMobile = window.innerWidth <= 768;
    _illusDragOffsetX = isMobile ? 0 : (_config?.illustration?.defaultOffsetX ?? 0);
    _illusDragOffsetY = isMobile ? 0 : (_config?.illustration?.defaultOffsetY ?? 0);
    applyIllusPosition();
  }

  function _doResetTextOffset() {
    _textOffsets = {};
    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      // image-left: reset整体text-container偏移
      const tc = c.querySelector('.prod-text-container');
      if (tc) { tc.style.left = '0px'; tc.style.top = '0px'; }
      // 其他布局: reset每行独立偏移
      c.querySelectorAll('.prod-text-line').forEach(tl => {
        tl.style.left = '0px'; tl.style.top = '0px';
      });
    });
    _updateDomDebugPanel();
  }

  function dom(id) { return document.getElementById(id); }

  document.addEventListener('click', function(e) {
    const t = e.target.closest('button');
    if (!t) return;
    if (t.id === 'prodSizeResetBtn')   { e.preventDefault(); e.stopPropagation(); _doResetSize();        return; }
    if (t.id === 'prodIllusResetBtn')  { e.preventDefault(); e.stopPropagation(); _doResetPosition();    return; }
    if (t.id === 'prodTextResetBtn')   { e.preventDefault(); e.stopPropagation(); _doResetTextOffset();  return; }
  }, true);

  function init(config, variants, creds) {
    _config=config; _variants=variants; _creds=creds;
    DEFAULT_PHOTO_SIZE = config.illustration?.defaultSize ?? 38;
    const _isMobileInit = window.innerWidth <= 768;
    _illusDragOffsetX = _isMobileInit ? 0 : (config.illustration?.defaultOffsetX ?? 0);
    _illusDragOffsetY = _isMobileInit ? 0 : (config.illustration?.defaultOffsetY ?? 0);
    // ★ 初始化时同步 X/Y 输入框（DOM 默认值是 0，但 config 可能有偏移量）
    requestAnimationFrame(() => {
      const xi = dom('prodIllusXInput'), yi = dom('prodIllusYInput');
      if (xi) xi.value = Math.round(_illusDragOffsetX);
      if (yi) yi.value = Math.round(_illusDragOffsetY);
    });
    selectedPhotoStyle = config.photo_processing?.style || 'line-art';
    curBorderStyle = config.border || 'none';

    if (!document.getElementById('_confirmLabelHide')) {
      const _hideStyle = document.createElement('style');
      _hideStyle.id = '_confirmLabelHide';
      _hideStyle.textContent = '#prodConfirmPreview ~ *, label:has(#prodConfirmPreview) { visibility: hidden !important; }';
      document.head.appendChild(_hideStyle);
    }

    const activeSize = document.querySelector('.prod-size-btn.active');
    const activeInk  = document.querySelector('.prod-ink-btn.active');
    selectedSize = activeSize?.dataset.size || '';
    selectedInk  = activeInk?.dataset.ink  || '';

    if (config.features?.enable_email && typeof emailjs!=='undefined')
      emailjs.init(creds.emailjsPublicKey);

    if (typeof window.StampImageProcessor!=='undefined' && creds.removebgKey)
      window.StampImageProcessor.REMOVEBG_API_KEY = creds.removebgKey;
    if (typeof window.StickerImageProcessor!=='undefined' && creds.removebgKey)
      window.StickerImageProcessor.REMOVEBG_API_KEY = creds.removebgKey;

    setupListeners();

    config.text_lines?.forEach((line, i) => {
      const picker = document.querySelector(`.prod-font-picker[data-line-index="${i}"]`);
      if (picker) picker.querySelectorAll('.prod-font-btn').forEach(b =>
        b.classList.toggle('active', b.dataset.font === (line.font||'Montserrat')));
      const hf = dom(`prodFontLine${i}`);
      if (hf) hf.value = line.font||'Montserrat';
      Core.updateSliderFill(dom(`prodFontSizeSlider${i}`));
      Core.updateSliderFill(dom(`prodFontWeightSlider${i}`));
    });

    const photoSl = dom('prodPhotoSizeSlider');
    if (photoSl) {
      photoSl.value = DEFAULT_PHOTO_SIZE;
      Core.updateSliderFill(photoSl);
      requestAnimationFrame(() => requestAnimationFrame(() =>
        setTimeout(() => updatePhotoCenterSize(DEFAULT_PHOTO_SIZE), 100)
      ));
    }

    if (config.layout==='curved-text' || config.layout==='photo-upload') {
      // ★ FIX: 下弧文字默认位置太靠下
      // radiusScale 越大弧半径越大，文字越靠近画布底边
      // 将 bottom-curve 的 radiusScale 上限限制为 0.92，避免文字贴近外圆底部
      config.text_lines?.forEach(line => {
        if (line.position === 'bottom-curve' || line.position === 'bottom') {
          if (!line.radiusScale || line.radiusScale > 0.92) {
            line.radiusScale = 0.92;
          }
        }
      });
      Core.initCurvedText();
    }

    if (config.layout==='body-template' && typeof window.StampBodyComposer!=='undefined') {
      window.StampBodyComposer.init(config, 'prodStampCanvas').then(ok => {
        if (ok) {
          window.StampBodyComposer.enableHeadDrag();
          window.StampBodyComposer.enableTextDrag();
          _showMobilePreviewAlways();
          _injectBodyComposerUI();
          _addBodyComposerPinchZoom();
        }
      });
    }

    if (config.layout==='address-label' && typeof window.StampAddressComposer!=='undefined') {
      window.StampAddressComposer.initUI(config, _variants, _creds);
    }

    requestAnimationFrame(()=>{
      if (config.border && config.border !== 'none') {
        const targetBtn = document.querySelector(`.prod-border-btn[data-border="${config.border}"]`);
        if (targetBtn) {
          document.querySelectorAll('.prod-border-btn').forEach(b => b.classList.remove('active'));
          targetBtn.classList.add('active');
        }
      }
      const ab = document.querySelector('.prod-border-btn.active');
      if (ab) updateBorderStyle(ab.dataset.border);
      ['prodBorderInsetSlider','prodBorderWidthSlider']
        .forEach(id => { const s=dom(id); if(s) Core.updateSliderFill(s); });
      applyBorderFromSliders();
      setTimeout(() => {
        if (config.border && config.border !== 'none') {
          const targetBtn2 = document.querySelector(`.prod-border-btn[data-border="${config.border}"]`);
          if (targetBtn2) {
            document.querySelectorAll('.prod-border-btn').forEach(b => b.classList.remove('active'));
            targetBtn2.classList.add('active');
          }
        }
        const ab2 = document.querySelector('.prod-border-btn.active');
        if (ab2 && ab2.dataset.border !== 'none') updateBorderStyle(ab2.dataset.border);
      }, 800);
    });

    updateVariant();

    // ★ 隐藏 DOUBLE 和 WAVE 边框按钮（Liquid 渲染在 HTML 里，只能用 JS 隐藏）
    // 若默认激活了被隐藏的按钮，自动切换到 'none'
    requestAnimationFrame(() => {
      ['double','floral2'].forEach(bdrVal => {
        document.querySelectorAll(`.prod-border-btn[data-border="${bdrVal}"]`).forEach(btn => {
          btn.style.display = 'none';
          if (btn.classList.contains('active')) {
            btn.classList.remove('active');
            document.querySelector('.prod-border-btn[data-border="none"]')?.classList.add('active');
            updateBorderStyle('none');
          }
        });
      });
    });

    const _cacheDesktopRef = () => {
      const dc = document.getElementById("prodStampCanvas");
      if (dc) {
        const w = dc.offsetWidth || dc.getBoundingClientRect().width || dc.scrollWidth;
        const h = dc.offsetHeight || dc.getBoundingClientRect().height || dc.scrollHeight;
        if (w > 0 && h > 0) {
          window._stampCanvasRefW = w;
          window._stampCanvasRefH = h;
          dc.dataset.cachedW = w;
          dc.dataset.cachedH = h;
        }
      }
    };
    _cacheDesktopRef();
    requestAnimationFrame(_cacheDesktopRef);
    [100, 300, 800, 1500, 3000].forEach(t => setTimeout(_cacheDesktopRef, t));
    updateInkColor(selectedInk);

    _relocatePhotoSizeSlider();
    _fixUploadHintOverflow();
    _fixMobileLabelLayout();
    _addPinchZoomSupport();
    _blockCanvasClickModal();
    _initTopPreviewSync();
    if (_config.layout === 'image-left') {
      setTimeout(setupIllusDragOnPreview, 350);
      setTimeout(_fitImageLeftText, 500);
      setTimeout(_fitImageLeftText, 1200);
    }
    if (_config.layout === 'text-only') {
      setTimeout(_fitTextOnlyCanvas, 400);
      setTimeout(_fitTextOnlyCanvas, 900);
    }
    setTimeout(_updateDomDebugPanel, 1500);
    const confirmCb = document.getElementById('prodConfirmPreview');
    if (confirmCb) {
      confirmCb.checked = true;
      const confirmLabel = confirmCb.closest('label') || confirmCb.parentElement;
      if (confirmLabel) {
        Array.from(confirmLabel.childNodes).forEach(node => {
          if (node !== confirmCb) node.remove();
        });
        const span = document.createElement('span');
        span.textContent = ' I have reviewed my stamp preview and confirm all information is correct.';
        span.style.cssText = 'font-size:inherit;color:inherit;';
        confirmLabel.appendChild(span);
      }
    }
    document.getElementById('_confirmLabelHide')?.remove();

    function _bindResetBtns() {
      const _sizeBtn = document.getElementById('prodSizeResetBtn');
      if (_sizeBtn && !_sizeBtn.dataset.resetBound) {
        _sizeBtn.dataset.resetBound = '1';
        _sizeBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); _doResetSize(); });
      }
      const _posBtn = document.getElementById('prodIllusResetBtn');
      if (_posBtn && !_posBtn.dataset.resetBound) {
        _posBtn.dataset.resetBound = '1';
        _posBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); _doResetPosition(); });
      }
    }
    _bindResetBtns();
    setTimeout(_bindResetBtns, 300);
    setTimeout(_bindResetBtns, 1000);

    // ★ v4.9: 注入 Review 按钮（在 Add to Cart 上方）
    _injectReviewBtn();
    setTimeout(_injectReviewBtn, 500); // 兜底，防止 DOM 未就绪
  }

  function _initTopPreviewSync() {
    if (_config.layout === 'body-template' || _config.layout === 'address-label') return;

    if (!Core.getAllCanvases._topPatched) {
      const orig = Core.getAllCanvases.bind(Core);
      Core.getAllCanvases = function() {
        const list = orig();
        const top = document.getElementById('prodMobileCanvasTop');
        if (top && !list.includes(top)) list.push(top);
        return list;
      };
      Core.getAllCanvases._topPatched = true;
    }

    const topEl = document.getElementById('prodMobileCanvasTop');
    if (topEl) {
      topEl.style.pointerEvents = 'none';
      if (_config.layout === 'curved-text' || _config.layout === 'photo-upload') {
        requestAnimationFrame(() => setTimeout(() => Core.regenerateCurvedText(topEl), 200));
      }
    }

    requestAnimationFrame(() => {
      updateInkColor(selectedInk);
      const ab = document.querySelector('.prod-border-btn.active');
      if (ab) updateBorderStyle(ab.dataset.border);
    });
  }

  function _blockCanvasClickModal() {
    document.getElementById('prodDlModalOverlay')?.remove();
    document.querySelector('.prod-dl-modal-overlay')?.remove();

    document.querySelectorAll(
      '.prod-preview-section input[type="file"],' +
      '.prod-mobile-preview-bottom input[type="file"],' +
      '.prod-mobile-preview-canvas input[type="file"]'
    ).forEach(inp => {
      inp.style.pointerEvents = 'none';
      inp.tabIndex = -1;
    });

    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      c.querySelectorAll(
        '.prod-photo-preview-area, .prod-illustration-center.prod-photo-preview, .prod-body-template-placeholder'
      ).forEach(area => {
        area.onclick = null;
        area.style.cursor = 'default';
      });
    });
  }

  function setupListeners() {
    document.querySelectorAll('.prod-size-btn').forEach(b => b.addEventListener('click', function(){
      document.querySelectorAll('.prod-size-btn').forEach(x=>x.classList.remove('active'));
      this.classList.add('active'); selectedSize=this.dataset.size; updateVariant();
    }));

    document.querySelectorAll('.prod-ink-btn').forEach(b => b.addEventListener('click', function(){
      document.querySelectorAll('.prod-ink-btn').forEach(x=>x.classList.remove('active'));
      this.classList.add('active'); selectedInk=this.dataset.ink; updateVariant(); updateInkColor(selectedInk);
    }));

    document.querySelectorAll('.prod-style-btn').forEach(b => b.addEventListener('click', async function(){
      document.querySelectorAll('.prod-style-btn').forEach(x=>x.classList.remove('active'));
      this.classList.add('active'); selectedPhotoStyle=this.dataset.style;
      const ps=dom('prodPhotoStyle'); if(ps) ps.value=selectedPhotoStyle;
      if (originalUploadedImage) await reprocessPhoto(selectedPhotoStyle);
    }));

    const pu=dom('prodPhotoUpload'); if(pu) pu.addEventListener('change', handlePhotoUpload);

    _config.text_lines?.forEach((line, i) => {
      const input=dom(`prodTextLine${i}`), cc=dom(`prodCharCount${i}`);
      if (input) input.addEventListener('input', function(){
        const rem=(line.maxLength||60)-this.value.length;
        if(cc){ cc.textContent=rem; cc.parentElement.classList.toggle('warning',rem<10); }
        updateTextLine(i, this.value);
      });

      const picker=document.querySelector(`.prod-font-picker[data-line-index="${i}"]`);
      if (picker) picker.querySelectorAll('.prod-font-btn').forEach(b => b.addEventListener('click', function(){
        picker.querySelectorAll('.prod-font-btn').forEach(x=>x.classList.remove('active'));
        this.classList.add('active');
        const hf=dom(`prodFontLine${i}`); if(hf) hf.value=this.dataset.font;
        updateTextFont(i, this.dataset.font);
        const fa=this.dataset.alsoLine; if(fa){
          const j=+fa;
          const hf2=dom(`prodFontLine${j}`); if(hf2) hf2.value=this.dataset.font;
          updateTextFont(j, this.dataset.font);
        }
      }));

      makeSlider(`prodFontSizeSlider${i}`, `prodFontSizeVal${i}`, 'px', v => {
        const hid=dom(`prodFontSizeLine${i}`); if(hid) hid.value=v+'px';
        updateTextFontSize(i, v);
        const sa=dom(`prodFontSizeSlider${i}`)&&dom(`prodFontSizeSlider${i}`).dataset.alsoLine;
        if(sa){const j=+sa;const h2=dom(`prodFontSizeLine${j}`);if(h2)h2.value=v+'px';updateTextFontSize(j,v);}
      });
      makeSlider(`prodFontWeightSlider${i}`, `prodFontWeightVal${i}`, '', v => {
        const hid=dom(`prodFontWeightLine${i}`); if(hid) hid.value=v;
        updateTextFontWeight(i, v);
        const wa=dom(`prodFontWeightSlider${i}`)&&dom(`prodFontWeightSlider${i}`).dataset.alsoLine;
        if(wa){const j=+wa;const h2=dom(`prodFontWeightLine${j}`);if(h2)h2.value=v;updateTextFontWeight(j,v);}
      });
      const lsSl=dom(`prodLetterSpaceSlider${i}`);
      if(lsSl) makeSlider(`prodLetterSpaceSlider${i}`,`prodLetterSpaceVal${i}`,'px',v=>{
        updateTextLetterSpacing(i,v);
        const la=lsSl.dataset.alsoLine; if(la) updateTextLetterSpacing(+la,v);
      });
    });

    makeSlider('prodPhotoSizeSlider','prodPhotoSizeVal','%', v => { updatePhotoCenterSize(v); requestAnimationFrame(_updateDomDebugPanel); });
    makeSlider('prodPhotoXSlider','prodPhotoXVal','', ()=>updatePhotoPosition());
    makeSlider('prodPhotoYSlider','prodPhotoYVal','', ()=>updatePhotoPosition());
    // ★ image-left X/Y 数字输入框 → 直接设置 _illusDragOffsetX/Y 并应用
    dom('prodIllusXInput')?.addEventListener('input', function() {
      const v = parseFloat(this.value);
      if (!isNaN(v)) { _illusDragOffsetX = v; applyIllusPosition(); }
    });
    dom('prodIllusYInput')?.addEventListener('input', function() {
      const v = parseFloat(this.value);
      if (!isNaN(v)) { _illusDragOffsetY = v; applyIllusPosition(); }
    });

    document.querySelectorAll('.prod-border-btn').forEach(b => b.addEventListener('click', function(){
      document.querySelectorAll('.prod-border-btn').forEach(x=>x.classList.remove('active'));
      this.classList.add('active'); updateBorderStyle(this.dataset.border);
    }));
    makeSlider('prodBorderInsetSlider','prodBorderInsetVal','px', v=>{curBorderInset=v; applyBorderToAll();});
    makeSlider('prodBorderWidthSlider','prodBorderWidthVal','px', v=>{curBorderWidth=v; applyBorderToAll();});

    // ★ v4.8.4 FIX: 彻底脱离原生 form submit
    // 把 submitBtn 改为 type=button，阻止所有原生 POST（包括 Shopify theme.js 的拦截）
    // 只用 click listener → handleFormSubmit，确保只有唯一一条触发路径
    const form=dom('prodStampForm');
    if(form) {
      form.setAttribute('action', '#');
      // 保留 form submit listener 作为兜底（键盘 Enter 触发时）
      form.addEventListener('submit', function(e) { e.preventDefault(); e.stopImmediatePropagation(); handleFormSubmit(e); });
    }
    const _submitBtn = dom('prodSubmitBtn');
    if (_submitBtn && !_submitBtn.dataset.uiClickBound) {
      _submitBtn.dataset.uiClickBound = '1';
      // 强制 type=button，彻底阻止原生 form submit 触发路径
      _submitBtn.setAttribute('type', 'button');
      _submitBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handleFormSubmit(e);
      });
    }

    if (_config.features?.enable_email) {
      const ep=dom('prodSendPreviewBtn'), epm=dom('prodSendPreviewBtnMobile');
      if(ep) ep.addEventListener('click', openEmailModal);
      if(epm) epm.addEventListener('click', openEmailModal);
      dom('prodModalClose')?.addEventListener('click', closeEmailModal);
      dom('prodModalCancel')?.addEventListener('click', closeEmailModal);
      dom('prodResultCloseBtn')?.addEventListener('click', closeEmailModal);
      dom('prodEmailForm')?.addEventListener('submit', handleEmailSubmit);
      dom('prodModalEmail')?.addEventListener('input', ()=>{
        dom('prodModalEmail').classList.remove('error');
        dom('prodEmailError')?.classList.remove('show');
      });
      dom('prodEmailModal')?.addEventListener('click', e=>{ if(e.target===dom('prodEmailModal')) closeEmailModal(); });
      document.addEventListener('keydown', e=>{ if(e.key==='Escape' && dom('prodEmailModal')?.classList.contains('show')) closeEmailModal(); });
    }

    // ★ v4.8.0: Crop 事件绑定已移至 stamp-crop.js，此处无需重复绑定
  }

  function makeSlider(sliderId, valId, suffix, cb) {
    const sl=dom(sliderId); if(!sl) return;
    Core.updateSliderFill(sl);
    sl.addEventListener('input', function(){
      const v=parseInt(this.value);
      const vEl=dom(valId); if(vEl) vEl.textContent=v+(suffix||'');
      Core.updateSliderFill(this);
      cb(v);
    });
  }

  function updateVariant() {
    const v=_variants.find(x=>x.option1===selectedSize && x.option2===selectedInk);
    const btn=dom('prodSubmitBtn'), price=dom('prodPriceDisplay'), vid=dom('prodVariantId');
    if (v) {
      if(vid) vid.value=v.id;
      if(price) price.innerHTML=v.available ? v.price_formatted : v.price_formatted+' <span style="color:var(--error-color);">— Sold Out</span>';
      if(btn){ btn.disabled=!v.available; btn.textContent=v.available?'🛒 Add to Cart':'Sold Out'; }
    } else {
      if(price) price.innerHTML='<span style="color:var(--error-color);">Configuration Unavailable</span>';
      if(btn){ btn.disabled=true; btn.textContent='Unavailable'; }
    }
  }

  function updateInkColor(inkName) {
    const color = Core.INK_COLOR_MAP[inkName]||'#000000';
    Core.getAllCanvases().forEach(c=>{ if(!c) return; c.querySelectorAll('.prod-text-line').forEach(l=>l.style.color=color); });
    const cls = {Black:'ink-black',Red:'ink-red',Blue:'ink-blue',Green:'ink-green',Purple:'ink-purple',Brown:'ink-brown','No ink-stamp only':'ink-black'}[inkName]||'ink-black';
    document.querySelectorAll('.prod-illustration-img').forEach(img=>{
      if (_config.layout === 'image-left' && img.closest('.prod-illustration-left')) return;
      img.classList.remove('ink-black','ink-red','ink-blue','ink-green','ink-purple','ink-brown');
      img.classList.add(cls);
    });
  }

  function updateTextLine(i, val) {
    Core.getAllCanvases().forEach(c=>{ if(!c) return;
      const tl=c.querySelector(`[data-line-index="${i}"]`); if(!tl) return;
      const display=val.trim()||(_config.text_lines[i]?.placeholder||'');
      if(tl.classList.contains('curved-top-curve')||tl.classList.contains('curved-bottom-curve')){
        const ts=tl.querySelector('.prod-curved-text'); if(ts) ts.textContent=display;
        Core.regenerateCurvedText(c);
      } else { const sp=tl.querySelector('span'); if(sp) sp.textContent=display; }
    });
    updateInkColor(selectedInk);
    if (_config.layout === "image-left") requestAnimationFrame(_fitImageLeftText);
    if (_config.layout === "text-only")  requestAnimationFrame(_fitTextOnlyCanvas);
  }

  function updateTextFont(i, font) {
    Core.getAllCanvases().forEach(c=>{ if(!c) return;
      const tl=c.querySelector(`[data-line-index="${i}"]`); if(!tl) return;
      tl.dataset.font=font;
      if(tl.classList.contains('curved-top-curve')||tl.classList.contains('curved-bottom-curve'))
        Core.regenerateCurvedText(c);
      else { const sp=tl.querySelector('span'); if(sp) sp.style.fontFamily=`'${font}',sans-serif`; }
    });
    if (_config.layout === "text-only") requestAnimationFrame(_fitTextOnlyCanvas);
  }

  function updateTextFontSize(i, px) {
    Core.getAllCanvases().forEach(c=>{ if(!c) return;
      const tl=c.querySelector(`[data-line-index="${i}"]`); if(!tl) return;
      tl.dataset.fontsize=px+'px';
      if(tl.classList.contains('curved-top-curve')||tl.classList.contains('curved-bottom-curve'))
        Core.regenerateCurvedText(c);
      else { const sp=tl.querySelector('span'); if(sp) sp.style.fontSize=px+'px'; }
    });
    if (_config.layout === "text-only") requestAnimationFrame(_fitTextOnlyCanvas);
  }

  function updateTextFontWeight(i, w) {
    Core.getAllCanvases().forEach(c=>{ if(!c) return;
      const tl=c.querySelector(`[data-line-index="${i}"]`); if(!tl) return;
      tl.dataset.fontweight=w;
      if(tl.classList.contains('curved-top-curve')||tl.classList.contains('curved-bottom-curve'))
        Core.regenerateCurvedText(c);
      else { const sp=tl.querySelector('span'); if(sp) sp.style.fontWeight=w; }
    });
    if (_config.layout === "text-only") requestAnimationFrame(_fitTextOnlyCanvas);
  }

  function updateTextLetterSpacing(i, v) {
    Core.getAllCanvases().forEach(c=>{ if(!c) return;
      const tl=c.querySelector(`[data-line-index="${i}"]`); if(!tl) return;
      tl.dataset.letterSpacing=v;
      const sp=tl.querySelector('span'); if(sp) sp.style.letterSpacing=v+'px';
    });
    if (_config.layout === "text-only") requestAnimationFrame(_fitTextOnlyCanvas);
  }

  const CANVAS_LOGICAL_SIZE = { desktop: 480, mobile: 220 };

  function getCanvasBase(c) {
    if (c.offsetWidth > 0 && c.offsetHeight > 0) return Math.min(c.offsetWidth, c.offsetHeight);
    return CANVAS_LOGICAL_SIZE.desktop;
  }

  function updatePhotoCenterSize(pct) {
    if (_config.layout === 'curved-text') return;
    const CROP_SHAPES = _getCropShapes();
    const shape = CROP_SHAPES[_cropShapeKey] || CROP_SHAPES.circle;
    Core.getAllCanvases().forEach(c=>{ if(!c) return;
      const base = getCanvasBase(c);
      const px = Math.round(base * pct / 100);
      c.querySelectorAll('.prod-illustration-center,.prod-illustration-left,.prod-illustration-top')
        .forEach(el=>{
          const isIllusLeft = el.classList.contains('prod-illustration-left');
          if (isIllusLeft) {
            // image-left: 直接设 width/height，不用 scale
            // 高度跟随 canvas 内容区，宽度 = 用户 slider（上限48%）
            const canvasEl = el.closest('.prod-stamp-canvas');
            const padT = canvasEl ? parseFloat(getComputedStyle(canvasEl).paddingTop    || '20') : 20;
            const padB = canvasEl ? parseFloat(getComputedStyle(canvasEl).paddingBottom || '20') : 20;
            const canvasH = canvasEl ? (canvasEl.offsetHeight || 0) : 0;
            const contentH = Math.max(60, canvasH - padT - padB);
            const newSize = Math.min(px, contentH);
            // ★ FIX: stamp-form.css 对手机端 .prod-illustration-left 设了
            //   width/height: 45px !important，普通 el.style.width 写法会被覆盖。
            //   改用 setProperty(..., 'important')，行内 !important 优先级高于
            //   样式表 !important，才能让 slider / pinch-zoom 真正生效。
            el.style.setProperty('width',  newSize + 'px', 'important');
            el.style.setProperty('height', newSize + 'px', 'important');
            el.style.flexShrink = '0';
            // 保留拖动 translate，清除 scale
            const tx = _illusDragOffsetX || 0;
            const ty = _illusDragOffsetY || 0;
            el.style.transform       = `translate(${tx}px,${ty}px)`;
            el.style.transformOrigin = 'center center';
            el.style.willChange      = 'transform';
            return;
          }
          if (!el.dataset.baseSizePx) {
            const natural = el.offsetWidth || px;
            el.dataset.baseSizePx = natural;
            el.style.width  = natural + 'px';
            el.style.height = natural + 'px';
          }
          const bpx = parseFloat(el.dataset.baseSizePx) || px;
          const scale = px / bpx;
          el.style.transform       = `translate(0px,0px) scale(${scale.toFixed(4)})`;
          el.style.transformOrigin = 'center center';
          el.style.willChange      = 'transform';
        });
      const ph = Math.round(px * shape.h / shape.w);
      c.querySelectorAll('.prod-photo-preview,.prod-photo-preview-area')
        .forEach(el=>{
          if (!el.dataset.baseSizePx) {
            const natural = el.offsetWidth || px;
            el.dataset.baseSizePx = natural;
            el.style.width = natural + 'px';
            el.style.height = Math.round(natural * shape.h / shape.w) + 'px';
            el.style.borderRadius = shape.radius;
            el.style.overflow = 'hidden';
          }
          const bpx = parseFloat(el.dataset.baseSizePx) || px;
          const scale = px / bpx;
          el.style.transform       = `scale(${scale.toFixed(4)})`;
          el.style.transformOrigin = 'center center';
          el.style.willChange      = 'transform';
        });
    });
  }

  function updatePhotoPosition() { applyPhotoPosition(); }

  function applyPhotoPosition() {
    const xSl = dom('prodPhotoXSlider');
    const ySl = dom('prodPhotoYSlider');
    const sliderOffX = xSl ? (+xSl.value - Math.round((+xSl.min + +xSl.max) / 2)) : 0;
    const sliderOffY = ySl ? (+ySl.value - Math.round((+ySl.min + +ySl.max) / 2)) : 0;
    const totalX = _previewDragOffsetX + sliderOffX;
    const totalY = _previewDragOffsetY + sliderOffY;
    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      c.querySelectorAll('.prod-illustration-center.prod-photo-preview img.prod-uploaded-photo, .prod-photo-preview-area img')
        .forEach(img => {
          if (img.classList.contains('prod-uploaded-photo') && _config.layout === 'curved-text') {
            img.style.transform = `translate(${totalX}px,${totalY}px) scale(${_curvedPhotoScale})`;
            img.style.transformOrigin = 'center center';
          } else {
            img.style.transform = `translate(${totalX}px,${totalY}px)`;
            img.style.objectFit = 'contain';
          }
        });
    });
    _updateDomDebugPanel(); // ★ FIX: refresh debug on photo position change
  }

  function applyIllusPosition() {
    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      c.querySelectorAll('.prod-illustration-left').forEach(el => {
        el.style.transform = `translate(${_illusDragOffsetX}px,${_illusDragOffsetY}px)`;
      });
    });
    // ★ 同步 X/Y 数字输入框
    const xi = dom('prodIllusXInput'), yi = dom('prodIllusYInput');
    if (xi && document.activeElement !== xi) xi.value = Math.round(_illusDragOffsetX);
    if (yi && document.activeElement !== yi) yi.value = Math.round(_illusDragOffsetY);
    _updateDomDebugPanel();
  }

  function setupIllusDragOnPreview() {
    if (_config.layout !== 'image-left') return;
    if (!window._illusDragListenersAttached) {
      window._illusDragListenersAttached = true;
      document.addEventListener('mousemove', e => {
        if (!_illusDragActive) return;
        const dx = e.clientX - _illusDragStartX, dy = e.clientY - _illusDragStartY;
        if (!_illusDragMoved && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
        _illusDragMoved = true;
        _illusDragOffsetX = _illusDragStartOX + dx;
        _illusDragOffsetY = _illusDragStartOY + dy;
        applyIllusPosition();
      });
      document.addEventListener('touchmove', e => {
        if (!_illusDragActive || e.touches.length > 1) return;
        const t = e.touches[0];
        const dx = t.clientX - _illusDragStartX, dy = t.clientY - _illusDragStartY;
        if (!_illusDragMoved && Math.abs(dy) > Math.abs(dx) + 8) { _illusDragActive = false; return; }
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        e.preventDefault();
        _illusDragMoved = true;
        _illusDragOffsetX = _illusDragStartOX + dx;
        _illusDragOffsetY = _illusDragStartOY + dy;
        applyIllusPosition();
      }, { passive: false });
      document.addEventListener('mouseup',  () => { _illusDragActive = false; _illusDragMoved = false; });
      document.addEventListener('touchend', () => { _illusDragActive = false; _illusDragMoved = false; });
    }
    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      c.querySelectorAll('.prod-illustration-left').forEach(el => {
        if (el.dataset.illusDragAttached) return;
        el.dataset.illusDragAttached = '1';
        el.style.cursor = 'grab'; el.style.touchAction = 'none'; el.style.userSelect = 'none';
        const onStart = e => {
          if (e.touches && e.touches.length > 1) return;
          _illusDragActive = true; _illusDragMoved = false;
          const t = e.touches ? e.touches[0] : e;
          _illusDragStartX = t.clientX; _illusDragStartY = t.clientY;
          _illusDragStartOX = _illusDragOffsetX; _illusDragStartOY = _illusDragOffsetY;
          if (!e.touches) el.style.cursor = 'grabbing';
        };
        el.addEventListener('mousedown', e => { e.preventDefault(); onStart(e); });
        el.addEventListener('touchstart', e => { e.preventDefault(); onStart(e); }, { passive: false });
        el.addEventListener('mouseup', () => { el.style.cursor = 'grab'; });
      });
    });
  }

  function applyPhotoShapeToPreview() {
    if (_config.layout === 'curved-text') return;
    const CROP_SHAPES = _getCropShapes();
    const shape = CROP_SHAPES[_cropShapeKey] || CROP_SHAPES.circle;
    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      c.querySelectorAll('.prod-photo-preview-area, .prod-illustration-center.prod-photo-preview').forEach(el => {
        const curW = parseFloat(el.style.width) || el.offsetWidth || 150;
        const newH = Math.round(curW * shape.h / shape.w);
        el.style.height=newH+'px'; el.style.borderRadius=shape.radius; el.style.overflow='hidden';
      });
    });
  }

  let _photoDragMoved = false;

  function setupPhotoDragOnPreview() {
    if (window._photoDragListenersAttached) { _refreshPhotoDragCursors(); return; }
    window._photoDragListenersAttached = true;

    document.addEventListener('mousemove', e => {
      if (!_previewDragActive) return;
      const dx = e.clientX - _previewDragStartX, dy = e.clientY - _previewDragStartY;
      if (!_photoDragMoved && Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      _photoDragMoved = true;
      _previewDragOffsetX = _previewDragStartOX + dx;
      _previewDragOffsetY = _previewDragStartOY + dy;
      applyPhotoPosition();
    });

    document.addEventListener('touchmove', e => {
      if (!_previewDragActive || e.touches.length > 1) return;
      const t = e.touches[0];
      const dx = t.clientX - _previewDragStartX, dy = t.clientY - _previewDragStartY;
      if (!_photoDragMoved && Math.abs(dy) > Math.abs(dx) + 5) { _previewDragActive=false; return; }
      if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
      e.preventDefault();
      _photoDragMoved = true;
      _previewDragOffsetX = _previewDragStartOX + dx;
      _previewDragOffsetY = _previewDragStartOY + dy;
      applyPhotoPosition();
    }, { passive: false });

    document.addEventListener('mouseup', () => {
      if (!_previewDragActive) return;
      _previewDragActive = false;
      document.querySelectorAll('.prod-photo-drag-target').forEach(el => el.style.cursor='grab');
      if (_photoDragMoved) { window._suppressNextPhotoClick=true; setTimeout(()=>{window._suppressNextPhotoClick=false;},80); }
      _photoDragMoved = false;
    });
    document.addEventListener('touchend', () => { _previewDragActive=false; _photoDragMoved=false; });

    _refreshPhotoDragCursors();
  }

  function _refreshPhotoDragCursors() {
    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      c.querySelectorAll('.prod-photo-preview-area, .prod-illustration-center.prod-photo-preview').forEach(area => {
        if (area.classList.contains('prod-photo-drag-target')) return;
        area.classList.add('prod-photo-drag-target');
        area.style.cursor='grab'; area.style.touchAction='none';
        const onStart = e => {
          if (e.touches && e.touches.length > 1) return;
          const img=area.querySelector('img'); if(!img) return;
          _previewDragActive=true; _photoDragMoved=false;
          const t=e.touches?e.touches[0]:e;
          _previewDragStartX=t.clientX; _previewDragStartY=t.clientY;
          _previewDragStartOX=_previewDragOffsetX; _previewDragStartOY=_previewDragOffsetY;
          if(!e.touches) area.style.cursor='grabbing';
        };
        area.addEventListener('mousedown', e=>{ e.preventDefault(); onStart(e); });
        area.addEventListener('mouseup', e => {
          if (!_previewDragActive || _photoDragMoved) return;
          const pu = dom('prodPhotoUpload'); if (pu) { pu.value = ''; pu.click(); }
        });
        area.addEventListener('touchstart', e=>{
          if (e.touches.length > 1) return;
          const img = area.querySelector('img');
          if (!img) return;
          _touchStartX = e.touches[0].clientX;
          _touchStartY = e.touches[0].clientY;
          _touchStartTime = Date.now();
          e.preventDefault();
          onStart(e);
        }, { passive: false });
        area.addEventListener('touchend', e => {
          const dx = Math.abs((e.changedTouches[0]?.clientX || 0) - (_touchStartX || 0));
          const dy = Math.abs((e.changedTouches[0]?.clientY || 0) - (_touchStartY || 0));
          const dt = Date.now() - (_touchStartTime || 0);
          if (dx < 8 && dy < 8 && dt < 400) {
            _previewDragActive = false;
            _photoDragMoved = false;
            const pu = dom('prodPhotoUpload');
            if (pu) { pu.value = ''; pu.click(); }
          }
        });
      });
    });
  }

  function setupTextDragOnPreview() {
    if (window._textDragListenersAttached) { _refreshTextDragTargets(); return; }
    window._textDragListenersAttached = true;

    const _isImageLeft = _config.layout === 'image-left';

    document.addEventListener('mousemove', e => {
      if (!_textDragActive) return;
      const dx=e.clientX-_textDragStartX, dy=e.clientY-_textDragStartY;
      if (!_textDragMoved && Math.abs(dx)<4 && Math.abs(dy)<4) return;
      _textDragMoved=true;
      if (_isImageLeft) {
        _textOffsets['all'] = {x:_textDragStartOX+dx, y:_textDragStartOY+dy};
        _applyTextContainerOffset();
      } else {
        _textOffsets[_textDragLineIndex]={x:_textDragStartOX+dx, y:_textDragStartOY+dy};
        applyTextOffset(_textDragLineIndex);
      }
    });
    document.addEventListener('touchmove', e => {
      if (!_textDragActive) return;
      const t=e.touches[0], dx=t.clientX-_textDragStartX, dy=t.clientY-_textDragStartY;
      if (!_textDragMoved && Math.abs(dx)<4 && Math.abs(dy)<4) return;
      if (!_textDragMoved && Math.abs(dy)>Math.abs(dx)+5) { _textDragActive=false; return; }
      e.preventDefault(); _textDragMoved=true;
      if (_isImageLeft) {
        _textOffsets['all'] = {x:_textDragStartOX+dx, y:_textDragStartOY+dy};
        _applyTextContainerOffset();
      } else {
        _textOffsets[_textDragLineIndex]={x:_textDragStartOX+dx, y:_textDragStartOY+dy};
        applyTextOffset(_textDragLineIndex);
      }
    }, { passive: false });
    document.addEventListener('mouseup', ()=>{ _textDragActive=false; document.querySelectorAll('.prod-text-drag-target').forEach(el=>el.style.cursor='grab'); _textDragMoved=false; });
    document.addEventListener('touchend', ()=>{ _textDragActive=false; _textDragMoved=false; });

    _refreshTextDragTargets();
    _injectPreviewHint();
  }

  function _applyTextContainerOffset() {
    const off = _textOffsets['all'] || {x:0,y:0};
    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      const tc = c.querySelector('.prod-text-container');
      if (!tc) return;
      tc.style.position = 'relative';
      tc.style.left = off.x + 'px';
      tc.style.top  = off.y + 'px';
    });
    _updateDomDebugPanel();
  }

  function applyTextOffset(i) {
    const off=_textOffsets[i]||{x:0,y:0};
    Core.getAllCanvases().forEach(c=>{ if(!c) return;
      const tl=c.querySelector(`[data-line-index="${i}"]`); if(!tl) return;
      tl.style.position='relative'; tl.style.left=off.x+'px'; tl.style.top=off.y+'px';
    });
    _updateDomDebugPanel();
  }

  function _refreshTextDragTargets() {
    Core.getAllCanvases().forEach(c => {
      if (!c) return;
      if (c.id === 'prodMobileCanvasTop') return;

      if (_config.layout === 'image-left') {
        const tc = c.querySelector('.prod-text-container');
        if (!tc || tc.dataset.textDragAttached) return;
        tc.dataset.textDragAttached = '1';
        tc.classList.add('prod-text-drag-target');
        tc.style.cursor = 'grab'; tc.style.userSelect = 'none'; tc.style.touchAction = 'none';
        const onStart = e => {
          if (e.touches && e.touches.length > 1) return;
          e.preventDefault();
          _textDragActive = true; _textDragMoved = false; _textDragLineIndex = -1;
          const cur = _textOffsets['all'] || {x:0,y:0};
          const t = e.touches ? e.touches[0] : e;
          _textDragStartX = t.clientX; _textDragStartY = t.clientY;
          _textDragStartOX = cur.x; _textDragStartOY = cur.y;
          if (!e.touches) tc.style.cursor = 'grabbing';
        };
        tc.addEventListener('mousedown', e => { e.preventDefault(); onStart(e); });
        tc.addEventListener('touchstart', onStart, { passive: false });
        tc.addEventListener('mouseup', () => { tc.style.cursor = 'grab'; });
      } else {
        c.querySelectorAll('.prod-text-line').forEach(tl => {
          if (tl.classList.contains('prod-text-drag-target')) return;
          tl.classList.add('prod-text-drag-target');
          tl.style.cursor='grab'; tl.style.userSelect='none'; tl.style.touchAction='none';
          const onStart=e=>{ e.preventDefault(); _textDragActive=true; _textDragMoved=false; _textDragLineIndex=parseInt(tl.dataset.lineIndex||'0'); const off=_textOffsets[_textDragLineIndex]||{x:0,y:0}; const t=e.touches?e.touches[0]:e; _textDragStartX=t.clientX; _textDragStartY=t.clientY; _textDragStartOX=off.x; _textDragStartOY=off.y; if(!e.touches) tl.style.cursor='grabbing'; };
          tl.addEventListener('mousedown', e=>{ e.preventDefault(); onStart(e); });
          tl.addEventListener('touchstart', onStart, { passive: false });
          tl.addEventListener('mouseup', () => { tl.style.cursor='grab'; });
        });
      }
    });
  }

  function _injectPreviewHint() {
    const previewSection=document.querySelector('.prod-preview-section');
    if (!previewSection||previewSection.querySelector('.prod-drag-hint')) return;
    const hint=document.createElement('p');
    hint.className='prod-drag-hint';
    hint.style.cssText='text-align:center;font-size:12px;color:#999;margin-top:10px;letter-spacing:0.3px;';
    hint.textContent='✦ Drag image or text in the preview to reposition';
    previewSection.appendChild(hint);
  }

  function _relocatePhotoSizeSlider() {
    if (_config.layout === 'body-template') return;
    if (document.getElementById('prodImageToolsGroup')) return;
    const photoSl = dom('prodPhotoSizeSlider');
    if (!photoSl) return;
    const originalGroup = photoSl.closest('.prod-option-group');

    const uploadRow = document.querySelector('.prod-file-input-row') ||
                      document.querySelector('.prod-file-input-wrapper');
    if (!uploadRow) return;

    const sizeGroup = document.createElement('div');
    sizeGroup.className = 'prod-image-size-group';
    sizeGroup.innerHTML = `
      <div class="prod-image-size-header">
        <span class="prod-image-size-label">Image Size</span>
        <span class="prod-image-size-val" id="prodPhotoSizeValNew">${photoSl.value || 70}%</span>
      </div>
      <input type="range"
        class="prod-image-size-slider"
        id="prodPhotoSizeSliderNew"
        min="${photoSl.min||20}" max="${photoSl.max||150}" value="${photoSl.value||70}" step="1">
    `;
    uploadRow.insertAdjacentElement('afterend', sizeGroup);

    const newSl  = dom('prodPhotoSizeSliderNew');
    const newVal = dom('prodPhotoSizeValNew');

    function syncFill(sl) {
      const pct = ((sl.value - sl.min) / (sl.max - sl.min) * 100).toFixed(1) + '%';
      sl.style.setProperty('--fill', pct);
    }
    syncFill(newSl);

    newSl.addEventListener('input', function () {
      const v = parseInt(this.value);
      if (newVal) newVal.textContent = v + '%';
      syncFill(this);
      photoSl.value = v;
      photoSl.dispatchEvent(new Event('input'));
    });

    if (originalGroup) originalGroup.style.display = 'none';
  }

  function _fixUploadHintOverflow() {
    const fileInput = document.querySelector('.prod-file-input');
    if (!fileInput) return;

    const spans = Array.from(fileInput.querySelectorAll('span'));
    const hintSpan = spans.find(s =>
      !s.classList.contains('prod-file-input-icon') &&
      /jpg|png|mb|max|drag/i.test(s.textContent)
    );

    if (hintSpan) {
      const hintText = hintSpan.textContent.trim();
      hintSpan.remove();

      const row = fileInput.closest('.prod-file-input-row') ||
                  fileInput.closest('.prod-file-input-wrapper')?.parentElement ||
                  fileInput.parentElement;
      if (row) {
        row.querySelector('.prod-file-hint')?.remove();
        const hint = document.createElement('span');
        hint.className = 'prod-file-hint';
        hint.textContent = hintText;
        row.appendChild(hint);
      }
    }

    fileInput.style.maxWidth = '100%';
    fileInput.style.overflow = 'hidden';

    const row = fileInput.closest('.prod-file-input-row');
    if (row) {
      row.style.overflow = 'hidden';
      row.style.maxWidth = '100%';
    }
  }

  function _fixMobileLabelLayout() {
    if (document.getElementById('_prodLabelFix')) return;
    const st = document.createElement('style');
    st.id = '_prodLabelFix';
    st.textContent = `
      @media (max-width: 768px) {
        .prod-text-row,
        .prod-text-input-row,
        .prod-option-row {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          flex-wrap: nowrap !important;
          gap: 6px !important;
          width: 100% !important;
        }
        .prod-text-row .prod-row-label,
        .prod-text-input-row .prod-row-label,
        .prod-option-row .prod-option-label,
        .prod-form-row .prod-label {
          flex: 0 0 auto !important;
          max-width: 110px !important;
          min-width: 0 !important;
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          font-size: 11px !important;
        }
        .prod-text-row input[type="text"],
        .prod-text-input-row input[type="text"],
        .prod-option-row input[type="text"] {
          flex: 1 1 auto !important;
          min-width: 0 !important;
          width: 0 !important;
        }
      }

      .prod-stamp-canvas[class*="border-"] .prod-text-container {
        padding-top:    calc(var(--border-inset, 10px) + var(--border-width, 2px) + 6px) !important;
        padding-bottom: calc(var(--border-inset, 10px) + var(--border-width, 2px) + 6px) !important;
        padding-left:   calc(var(--border-inset, 10px) + var(--border-width, 2px) + 10px) !important;
        padding-right:  calc(var(--border-inset, 10px) + var(--border-width, 2px) + 10px) !important;
        box-sizing: border-box !important;
        width: 100% !important;
      }

      .prod-stamp-canvas[class*="border-double"] .prod-text-container,
      .prod-stamp-canvas.border-thick              .prod-text-container {
        padding-top:    calc(var(--border-inset, 10px) + var(--border-width, 2px) * 2 + 10px) !important;
        padding-bottom: calc(var(--border-inset, 10px) + var(--border-width, 2px) * 2 + 10px) !important;
        padding-left:   calc(var(--border-inset, 10px) + var(--border-width, 2px) * 2 + 14px) !important;
        padding-right:  calc(var(--border-inset, 10px) + var(--border-width, 2px) * 2 + 14px) !important;
      }

      .prod-stamp-canvas[class*="border-"] .prod-text-line span {
        display: block !important;
        max-width: 100% !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
      }
    `;
    document.head.appendChild(st);
  }

  function _addBodyComposerPinchZoom() {
    function getCanvas() { return dom('prodStampCanvas-composer'); }
    let _active = false, _startDist = 0, _startScale = 1.5;
    function getDist(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx*dx + dy*dy);
    }
    function attach(el) {
      if (!el || el.dataset.bodyPinchAttached) return;
      el.dataset.bodyPinchAttached = '1';
      el.addEventListener('touchstart', e => {
        if (e.touches.length === 2) {
          _active = true; _startDist = getDist(e.touches);
          const sl = dom('prodHeadSizeSlider');
          _startScale = sl ? parseInt(sl.value) / 100 : 1.5;
          e.preventDefault();
        }
      }, { passive: false });
      el.addEventListener('touchmove', e => {
        if (!_active || e.touches.length !== 2) return;
        e.preventDefault();
        const ratio    = getDist(e.touches) / _startDist;
        const newScale = Math.max(0.3, Math.min(3.0, _startScale * ratio));
        const newPct   = Math.round(newScale * 100);
        const sl  = dom('prodHeadSizeSlider');
        const val = dom('prodHeadSizeVal');
        if (sl)  sl.value = newPct;
        if (val) val.textContent = newPct + '%';
        if (window.StampBodyComposer) window.StampBodyComposer.setHeadScale(newScale);
      }, { passive: false });
      el.addEventListener('touchend',   (e) => { if (e.touches.length < 2) _active = false; });
      el.addEventListener('touchcancel',() => { _active = false; });
    }
    const c = getCanvas();
    if (c) attach(c);
    const slot = dom('prodMobileCanvasSlot');
    if (slot) {
      new MutationObserver(() => { const c2 = getCanvas(); if (c2) attach(c2); })
        .observe(slot, { childList: true, subtree: true });
    }
  }

  function _addPinchZoomSupport() {
    if (window._pinchZoomListenerAttached) return;
    window._pinchZoomListenerAttached = true;

    let _pinchActive = false, _pinchStartDist = 0, _pinchStartPct = 100;

    function getTouchDist(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx*dx + dy*dy);
    }
    function getCurrentPct() {
      if (_config.layout === 'curved-text') {
        const sl = dom('prodCurvedSizeSlider'); return sl ? parseInt(sl.value) : 100;
      }
      const sl = dom('prodPhotoSizeSliderNew') || dom('prodPhotoSizeSlider');
      return sl ? parseInt(sl.value) : 75;
    }
    function applyPct(newPct) {
      if (_config.layout === 'curved-text') {
        newPct = Math.max(30, Math.min(300, newPct));
        if (typeof window._curvedPinchScaleForSlider === 'function')
          window._curvedPinchScaleForSlider(newPct);
        return;
      }
      const sl    = dom('prodPhotoSizeSliderNew') || dom('prodPhotoSizeSlider');
      const origSl= dom('prodPhotoSizeSlider');
      if (!sl) return;
      newPct = Math.max(+sl.min, Math.min(+sl.max, newPct));
      sl.value = newPct;
      const fillPct = ((newPct - +sl.min) / (+sl.max - +sl.min) * 100).toFixed(1) + '%';
      sl.style.setProperty('--fill', fillPct);
      ['prodPhotoSizeValNew','prodPhotoSizeVal'].forEach(id => {
        const el = dom(id); if (el) el.textContent = newPct + '%';
      });
      if (origSl && origSl !== sl) { origSl.value = newPct; origSl.dispatchEvent(new Event('input')); }
      else sl.dispatchEvent(new Event('input'));
    }

    function attachToArea(area) {
      if (area.dataset.pinchAttached) return;
      area.dataset.pinchAttached = '1';
      area.addEventListener('touchstart', e => {
        if (e.touches.length === 2) {
          const t2 = e.touches[1];
          const textContainer = document.elementFromPoint(t2.clientX, t2.clientY)?.closest('.prod-text-container');
          if (textContainer && _config.layout === 'image-left') {
            _textDragActive = true; _textDragMoved = false; _textDragLineIndex = -1;
            const cur = _textOffsets['all'] || {x:0,y:0};
            _textDragStartX = t2.clientX; _textDragStartY = t2.clientY;
            _textDragStartOX = cur.x; _textDragStartOY = cur.y;
            e.preventDefault(); return;
          }
          _pinchActive = true; _previewDragActive = false;
          _pinchStartDist = getTouchDist(e.touches);
          _pinchStartPct  = getCurrentPct();
          e.preventDefault();
        }
      }, { passive: false });
      area.addEventListener('touchmove', e => {
        if (_textDragActive && _textDragLineIndex === -1 && e.touches.length >= 2) {
          e.preventDefault();
          const t2 = e.touches[1];
          const dx = t2.clientX - _textDragStartX, dy = t2.clientY - _textDragStartY;
          if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
            _textDragMoved = true;
            _textOffsets['all'] = {x: _textDragStartOX + dx, y: _textDragStartOY + dy};
            _applyTextContainerOffset();
          }
          return;
        }
        if (!_pinchActive || e.touches.length !== 2) return;
        e.preventDefault();
        const ratio  = getTouchDist(e.touches) / _pinchStartDist;
        const newPct = Math.round(_pinchStartPct * ratio);
        applyPct(newPct);
      }, { passive: false });
      area.addEventListener('touchend', e => { if (e.touches.length < 2) _pinchActive = false; });
      area.addEventListener('touchcancel', () => { _pinchActive = false; });
    }

    function attachAll() {
      Core.getAllCanvases().forEach(c => {
        if (!c) return;
        if (_config.layout === 'image-left') c.querySelectorAll('.prod-illustration-left').forEach(attachToArea);
        c.querySelectorAll('.prod-photo-preview-area, .prod-illustration-center.prod-photo-preview').forEach(attachToArea);
      });
    }

    const observer = new MutationObserver(attachAll);
    document.querySelectorAll('.prod-preview-section, .prod-stamp-canvas-wrapper, #prodMobileCanvasSlot, .prod-mobile-preview-bottom')
      .forEach(el => { if (el) observer.observe(el, { childList: true, subtree: true }); });
    attachAll();
    window._attachPinchZoomToPhotos = attachAll;
  }

  function _injectUploadZoneCSS() {
    if (document.getElementById('prodUploadZoneOverride')) return;
    const style = document.createElement('style');
    style.id = 'prodUploadZoneOverride';
    style.textContent = `
      .prod-file-input-wrapper,.prod-drop-zone,.prod-upload-zone { min-height:auto!important; padding:10px 16px!important; }
      .prod-file-input-wrapper .prod-upload-hint,.prod-file-input-wrapper .prod-drop-hint,
      .prod-drop-zone .prod-drop-hint,.prod-upload-zone .prod-upload-hint { display:none!important; }
    `;
    document.head.appendChild(style);
  }

  let _curvedPhotoScale = 1.0;

  function _applyCurvedPhotoScale() {
    Core.getAllCanvases().forEach(function(c) {
      if (!c) return;
      c.querySelectorAll('img.prod-uploaded-photo').forEach(function(img) {
        const tx = _previewDragOffsetX, ty = _previewDragOffsetY;
        img.style.transform = `translate(${tx}px,${ty}px) scale(${_curvedPhotoScale})`;
        img.style.transformOrigin = 'center center';
      });
      const ec = c.querySelector('canvas.prod-erase-canvas');
      if (ec) _redrawEraseCanvas(ec);
    });
  }

  let _eraseMode   = false;
  let _eraseSize   = 20;
  let _erasePainting = false;

  function _redrawEraseCanvas(ec) {
    if (!ec || !ec._photoImg) return;
    const ctx = ec.getContext('2d');
    ctx.clearRect(0, 0, ec.width, ec.height);
    const img = ec._photoImg;
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const fitScale = Math.min(ec.width / iw, ec.height / ih);
    const dw = iw * fitScale * _curvedPhotoScale;
    const dh = ih * fitScale * _curvedPhotoScale;
    const tx = ec.width  / 2 + _previewDragOffsetX;
    const ty = ec.height / 2 + _previewDragOffsetY;
    ctx.save();
    ctx.translate(tx, ty);
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
    if (ec._eraseMask) {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(ec._eraseMask, 0, 0, ec.width, ec.height);
      ctx.restore();
    }
  }

  function _ensureEraseCanvas() {
    Core.getAllCanvases().forEach(function(c) {
      if (!c) return;
      const photoArea = c.querySelector('.prod-illustration-center.prod-photo-preview');
      if (!photoArea) return;
      let ec = photoArea.querySelector('canvas.prod-erase-canvas');
      if (!ec) {
        ec = document.createElement('canvas');
        ec.className = 'prod-erase-canvas';
        // ★ v4.8.3 FIX: 手机端 photoArea 在 display:none 时 offsetWidth=0
        // getBoundingClientRect + offsetWidth 取较大值，确保尺寸非零
        const _paR = photoArea.getBoundingClientRect();
        const _paSz = Math.round(Math.max(_paR.width, _paR.height, photoArea.offsetWidth, photoArea.offsetHeight, 360));
        ec.width  = _paSz;
        ec.height = _paSz;
        ec.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;z-index:1;cursor:crosshair;display:none;border-radius:50%;';
        photoArea.appendChild(ec);

        function doErase(clientX, clientY) {
          if (!ec._eraseMask) {
            ec._eraseMask = document.createElement('canvas');
            ec._eraseMask.width  = ec.width;
            ec._eraseMask.height = ec.height;
          }
          const rect = ec.getBoundingClientRect();
          const scaleX = ec.width / rect.width, scaleY = ec.height / rect.height;
          const px = (clientX - rect.left) * scaleX;
          const py = (clientY - rect.top)  * scaleY;
          const mCtx = ec._eraseMask.getContext('2d');
          mCtx.globalCompositeOperation = 'source-over';
          mCtx.fillStyle = 'black';
          mCtx.beginPath();
          mCtx.arc(px, py, _eraseSize * Math.max(scaleX, scaleY), 0, Math.PI * 2);
          mCtx.fill();
          _redrawEraseCanvas(ec);
        }

        ec.addEventListener('mousedown',  e => { if (!_eraseMode) return; _erasePainting = true; doErase(e.clientX, e.clientY); e.stopPropagation(); });
        ec.addEventListener('mousemove',  e => { if (!_eraseMode || !_erasePainting) return; doErase(e.clientX, e.clientY); });
        ec.addEventListener('mouseup',    () => { _erasePainting = false; });
        ec.addEventListener('touchstart', e => { if (!_eraseMode || e.touches.length !== 1) return; _erasePainting = true; doErase(e.touches[0].clientX, e.touches[0].clientY); e.preventDefault(); e.stopPropagation(); }, { passive: false });
        ec.addEventListener('touchmove',  e => { if (!_eraseMode || !_erasePainting || e.touches.length !== 1) return; doErase(e.touches[0].clientX, e.touches[0].clientY); e.preventDefault(); }, { passive: false });
        ec.addEventListener('touchend',   () => { _erasePainting = false; });
      }
      const img = photoArea.querySelector('img.prod-uploaded-photo');
      if (img && img.src && ec._photoImg?.src !== img.src) {
        const i = new Image(); i.crossOrigin = 'anonymous';
        i.onload = () => { ec._photoImg = i; _redrawEraseCanvas(ec); };
        i.src = img.src;
      }
    });
  }

  function _injectCurvedPhotoUI() {
    const uploadGroup = document.querySelector('.prod-option-group:has(#prodPhotoUpload)') ||
                        document.querySelector('.prod-file-input-wrapper')?.closest('.prod-option-group');
    if (!uploadGroup) return;
    _injectUploadZoneCSS();

    const tools = document.createElement('div');
    tools.id = 'prodCurvedPhotoTools';
    tools.style.cssText = 'margin-top:16px;';
    tools.innerHTML = `
      <div style="padding:14px 16px;background:#f8f4ff;border-radius:12px;border:1.5px solid #e0d5f0;margin-bottom:10px;">
        <div style="font-size:13px;font-weight:700;color:#5a3e8a;margin-bottom:12px;">🖼 Photo Tools</div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;align-items:end;">
          <div style="display:flex;flex-direction:column;gap:6px;">
            <div style="font-size:11px;font-weight:700;color:#5a3e8a;">🔍 Image Size <span id="prodCurvedSizeVal" style="color:#7c3aed;">100%</span></div>
            <input type="range" id="prodCurvedSizeSlider" min="30" max="300" value="100" step="1" style="width:100%;accent-color:#7c3aed;cursor:pointer;">
            <button type="button" id="prodCurvedResetSize" style="width:100%;padding:6px 4px;border-radius:8px;border:1.5px solid #d6cfe8;background:white;color:#888;font-weight:600;font-size:11px;cursor:pointer;">↺ Reset Size</button>
          </div>
          <div style="display:flex;flex-direction:column;gap:6px;">
            <button type="button" id="prodCurvedModeMove" style="width:100%;padding:7px 4px;border-radius:8px;border:2px solid #6c3fc5;background:#6c3fc5;color:white;font-weight:600;font-size:12px;cursor:pointer;">✋ Move</button>
            <div style="flex:1;min-height:28px;"></div>
            <button type="button" id="prodCurvedResetMove" style="width:100%;padding:6px 4px;border-radius:8px;border:1.5px solid #d6cfe8;background:white;color:#888;font-weight:600;font-size:11px;cursor:pointer;">↺ Reset Move</button>
          </div>
          <div style="display:flex;flex-direction:column;gap:6px;">
            <button type="button" id="prodCurvedModeEraser" style="width:100%;padding:7px 4px;border-radius:8px;border:2px solid #d6cfe8;background:#f3f0f8;color:#7a6a9a;font-weight:600;font-size:12px;cursor:pointer;">🧹 Erase</button>
            <div id="prodCurvedEraserSizeGroup" style="display:none;flex-direction:column;gap:4px;">
              <div style="display:flex;justify-content:space-between;font-size:11px;color:#888;"><span>Eraser Size</span><span id="prodCurvedEraserSizeVal">20px</span></div>
              <input type="range" id="prodCurvedEraserSizeSlider" min="5" max="80" step="1" value="20" style="width:100%;accent-color:#7c3aed;">
            </div>
            <button type="button" id="prodCurvedResetErase" style="width:100%;padding:6px 4px;border-radius:8px;border:1.5px solid #d6cfe8;background:white;color:#888;font-weight:600;font-size:11px;cursor:pointer;">↺ Reset Erase</button>
          </div>
        </div>
      </div>
    `;
    uploadGroup.insertAdjacentElement('afterend', tools);

    dom('prodCurvedSizeSlider')?.addEventListener('input', function() {
      _curvedPhotoScale = parseInt(this.value) / 100;
      dom('prodCurvedSizeVal').textContent = this.value + '%';
      _applyCurvedPhotoScale();
    });

    window._curvedPinchScaleForSlider = function(newPct) {
      _curvedPhotoScale = newPct / 100;
      const sl = dom('prodCurvedSizeSlider'), vl = dom('prodCurvedSizeVal');
      if (sl) sl.value = newPct; if (vl) vl.textContent = newPct + '%';
      _applyCurvedPhotoScale();
    };

    const btnMove = dom('prodCurvedModeMove'), btnErase = dom('prodCurvedModeEraser');
    const sizeGroup = dom('prodCurvedEraserSizeGroup');

    function setMode(mode) {
      _eraseMode = (mode === 'eraser');
      if (_eraseMode) {
        _ensureEraseCanvas();
        Core.getAllCanvases().forEach(function(c) {
          const ec = c.querySelector('canvas.prod-erase-canvas');
          if (ec) ec.style.display = 'block';
          const img = c.querySelector('img.prod-uploaded-photo');
          if (img) img.style.visibility = 'hidden';
          const pa = c.querySelector('.prod-illustration-center.prod-photo-preview');
          if (pa) pa.style.cursor = 'crosshair';
        });
      } else {
        Core.getAllCanvases().forEach(function(c) {
          const ec = c.querySelector('canvas.prod-erase-canvas');
          if (ec) ec.style.display = 'none';
          const img = c.querySelector('img.prod-uploaded-photo');
          if (img) img.style.visibility = 'visible';
          const pa = c.querySelector('.prod-illustration-center.prod-photo-preview');
          if (pa) pa.style.cursor = 'grab';
        });
      }
      btnMove.style.cssText  = `padding:7px 16px;border-radius:8px;font-weight:600;font-size:12.5px;cursor:pointer;border:2px solid ${_eraseMode?'#d6cfe8':'#6c3fc5'};background:${_eraseMode?'#f3f0f8':'#6c3fc5'};color:${_eraseMode?'#7a6a9a':'white'};`;
      btnErase.style.cssText = `padding:7px 16px;border-radius:8px;font-weight:600;font-size:12.5px;cursor:pointer;border:2px solid ${_eraseMode?'#6c3fc5':'#d6cfe8'};background:${_eraseMode?'#6c3fc5':'#f3f0f8'};color:${_eraseMode?'white':'#7a6a9a'};`;
      if (sizeGroup) sizeGroup.style.display = _eraseMode ? 'flex' : 'none';
    }

    btnMove?.addEventListener('click',   () => setMode('move'));
    btnErase?.addEventListener('click',  () => setMode('eraser'));

    dom('prodCurvedEraserSizeSlider')?.addEventListener('input', function() {
      _eraseSize = +this.value;
      dom('prodCurvedEraserSizeVal').textContent = this.value + 'px';
    });

    dom('prodCurvedResetSize')?.addEventListener('click', () => {
      _curvedPhotoScale = 1.0;
      const sl = dom('prodCurvedSizeSlider'), vl = dom('prodCurvedSizeVal');
      if (sl) sl.value = 100; if (vl) vl.textContent = '100%';
      _applyCurvedPhotoScale();
      Core.showMessage('success', '↺ Image size reset to 100%');
    });
    dom('prodCurvedResetMove')?.addEventListener('click', () => {
      _previewDragOffsetX = 0; _previewDragOffsetY = -30;
      applyPhotoPosition();
      Core.showMessage('success', '↺ Position reset');
    });
    dom('prodCurvedResetErase')?.addEventListener('click', () => {
      Core.getAllCanvases().forEach(function(c) {
        const ec = c.querySelector('canvas.prod-erase-canvas');
        if (ec) { ec._eraseMask = null; ec._photoImg = null; ec.remove(); }
        const img = c.querySelector('img.prod-uploaded-photo');
        if (img) img.style.visibility = 'visible';
      });
      setMode('move');
      Core.showMessage('success', '↺ Erase edits cleared');
    });

    setMode('move');
  }

  function _injectBodyComposerUI() {
    const uploadGroup = document.querySelector('.prod-option-group:has(#prodPhotoUpload)') ||
                        document.querySelector('.prod-file-input-wrapper')?.closest('.prod-option-group');
    if (!uploadGroup || document.getElementById('prodBodyComposerTools')) return;
    _injectUploadZoneCSS();
    const tools = document.createElement('div');
    tools.id = 'prodBodyComposerTools';
    tools.style.cssText = 'display:none; margin-top:16px;';
    tools.innerHTML = `
      <div style="padding:12px 14px;background:#f8f4ff;border-radius:12px;border:1.5px solid #e0d5f0;margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <span style="font-size:13px;font-weight:700;color:#5a3e8a;">🔍 Image Size</span>
          <span id="prodHeadSizeVal" style="font-size:12px;color:#666;font-weight:600;">150%</span>
        </div>
        <input type="range" id="prodHeadSizeSlider" min="30" max="300" value="150" step="1" style="width:100%;accent-color:#7c3aed;cursor:pointer;">
        <p style="font-size:11px;color:#aaa;margin:5px 0 0;">Switch to Move mode to drag image position</p>
      </div>
      <div id="prodEraserToolbar" style="padding:14px 16px;background:#f8f4ff;border-radius:12px;border:1.5px solid #e0d5f0;margin-bottom:10px;">
        <div style="font-size:13px;font-weight:700;color:#5a3e8a;margin-bottom:10px;">🖼 Photo Tools</div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:10px;">
          <button type="button" id="prodModeMove" style="padding:7px 16px;border-radius:8px;border:2px solid #6c3fc5;background:#6c3fc5;color:white;font-weight:600;font-size:12.5px;cursor:pointer;">✋ Move</button>
          <button type="button" id="prodModeEraser" style="padding:7px 16px;border-radius:8px;border:2px solid #d6cfe8;background:#f3f0f8;color:#7a6a9a;font-weight:600;font-size:12.5px;cursor:pointer;">🧹 Erase</button>
          <button type="button" id="prodEraserReset" style="padding:7px 13px;border-radius:8px;border:1.5px solid #d6cfe8;background:white;color:#888;font-weight:600;font-size:12px;cursor:pointer;">↺ Reset</button>
        </div>
        <div id="prodEraserSizeGroup" style="display:none;">
          <div style="display:flex;justify-content:space-between;font-size:12px;color:#888;margin-bottom:4px;"><span>Eraser Size</span><span id="prodEraserSizeVal">20px</span></div>
          <input type="range" id="prodEraserSizeSlider" min="5" max="80" step="1" value="20" style="width:100%;accent-color:#7c3aed;">
        </div>
        <div style="font-size:11px;color:#aaa;margin-top:8px;">✏️ Erase to clean edges · Move to drag image &amp; text</div>
      </div>
      <div style="padding:12px 14px;background:#f8f4ff;border-radius:12px;border:1.5px solid #e0d5f0;">
        <div style="font-size:13px;font-weight:700;color:#5a3e8a;margin-bottom:10px;">✏️ Custom Text</div>
        <input type="text" id="prodCustomTextInput" placeholder="e.g. CUSTOM TEXT" style="width:100%;padding:8px 10px;border-radius:8px;border:1.5px solid #d0c4e8;font-size:14px;box-sizing:border-box;outline:none;font-family:inherit;">
        <div style="display:flex;gap:8px;margin-top:10px;flex-wrap:wrap;align-items:center;">
          <select id="prodCustomTextFont" style="flex:1;min-width:120px;padding:6px 8px;border-radius:8px;border:1.5px solid #d0c4e8;font-size:12px;background:white;cursor:pointer;">
            <option value="Montserrat">Montserrat</option><option value="Playfair Display">Playfair Display</option>
            <option value="Roboto">Roboto</option><option value="Open Sans">Open Sans</option>
            <option value="Oswald">Oswald</option><option value="Lato">Lato</option>
            <option value="Raleway">Raleway</option><option value="Dancing Script">Dancing Script</option>
            <option value="Pacifico">Pacifico</option><option value="Anton">Anton</option>
          </select>
          <div style="display:flex;align-items:center;gap:4px;">
            <span style="font-size:11px;color:#888;">Size</span>
            <input type="number" id="prodCustomTextSize" value="36" min="10" max="100" style="width:56px;padding:5px 6px;border-radius:8px;border:1.5px solid #d0c4e8;font-size:12px;text-align:center;">
          </div>
          <input type="color" id="prodCustomTextColor" value="#000000" style="width:34px;height:34px;border-radius:8px;border:1.5px solid #d0c4e8;padding:2px;cursor:pointer;background:white;">
        </div>
        <div style="margin-top:12px;">
          <div style="display:flex;justify-content:space-between;font-size:12px;color:#888;margin-bottom:4px;"><span>↗ Text Curve</span><span id="prodTextCurveVal" style="color:#7c3aed;font-weight:600;">25°</span></div>
          <input type="range" id="prodTextCurveSlider" min="-60" max="60" value="25" step="1" style="width:100%;accent-color:#7c3aed;cursor:pointer;">
        </div>
        <p style="font-size:11px;color:#aaa;margin:7px 0 0;">Switch to Move mode · drag text to reposition</p>
      </div>
    `;
    uploadGroup.insertAdjacentElement('afterend', tools);

    dom('prodHeadSizeSlider')?.addEventListener('input', function() {
      const pct = parseInt(this.value); dom('prodHeadSizeVal').textContent = pct + '%';
      window.StampBodyComposer.setHeadScale(pct / 100);
    });
    dom('prodCustomTextInput')?.addEventListener('input', function() { window.StampBodyComposer.setCustomText(this.value); });
    dom('prodCustomTextFont')?.addEventListener('change', function() { window.StampBodyComposer.setCustomFont(this.value); });
    dom('prodCustomTextSize')?.addEventListener('input', function() { window.StampBodyComposer.setCustomFontSize(parseInt(this.value) || 36); });
    dom('prodCustomTextColor')?.addEventListener('input', function() { window.StampBodyComposer.setCustomColor(this.value); });
    dom('prodTextCurveSlider')?.addEventListener('input', function() {
      const deg = parseInt(this.value); dom('prodTextCurveVal').textContent = deg + '°';
      window.StampBodyComposer.setCustomTextCurve(deg);
    });

    const btnEraser = dom('prodModeEraser'), btnMove = dom('prodModeMove'), sizeGroup = dom('prodEraserSizeGroup');
    const setMode = (mode) => {
      const isErase = mode === 'eraser';
      if (window.StampBodyComposer && typeof window.StampBodyComposer.setEraserActive === 'function')
        window.StampBodyComposer.setEraserActive(isErase);
      btnMove.style.cssText   = `padding:7px 16px;border-radius:8px;font-weight:600;font-size:12.5px;cursor:pointer;border:2px solid ${isErase?'#d6cfe8':'#6c3fc5'};background:${isErase?'#f3f0f8':'#6c3fc5'};color:${isErase?'#7a6a9a':'white'};`;
      btnEraser.style.cssText = `padding:7px 16px;border-radius:8px;font-weight:600;font-size:12.5px;cursor:pointer;border:2px solid ${isErase?'#6c3fc5':'#d6cfe8'};background:${isErase?'#6c3fc5':'#f3f0f8'};color:${isErase?'white':'#7a6a9a'};`;
      if (sizeGroup) sizeGroup.style.display = isErase ? 'block' : 'none';
    };
    btnEraser?.addEventListener('click', () => setMode('eraser'));
    btnMove?.addEventListener('click',   () => setMode('move'));
    dom('prodEraserSizeSlider')?.addEventListener('input', function() {
      dom('prodEraserSizeVal').textContent = this.value + 'px';
      window.StampBodyComposer.setEraserSize(+this.value);
    });
    dom('prodEraserReset')?.addEventListener('click', () => {
      window.StampBodyComposer.resetEraser();
      Core.showMessage('success', '↺ Brush edits reset · Position & size preserved');
    });
    setMode('move');
  }

  function _showMobilePreviewAlways() {
    if (dom('prodMobilePreviewAboveUpload')) return;
    const uploadGroup = document.querySelector('.prod-option-group:has(#prodPhotoUpload)') ||
                        document.querySelector('.prod-file-input-wrapper')?.closest('.prod-option-group');
    if (!uploadGroup) return;
    const wrapper = document.createElement('div');
    wrapper.id = 'prodMobilePreviewAboveUpload';
    wrapper.style.cssText = 'display:none; margin-bottom:16px; text-align:center;';
    wrapper.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;gap:6px;margin-bottom:10px;">
        <div style="flex:1;height:1px;background:#e0d5f0;"></div>
        <span style="font-size:11px;color:#8b6ba3;font-weight:700;letter-spacing:1px;white-space:nowrap;">📋 LIVE PREVIEW</span>
        <div style="flex:1;height:1px;background:#e0d5f0;"></div>
      </div>
      <div id="prodMobileCanvasSlot" style="display:inline-block;width:100%;max-width:320px;border-radius:16px;border:2px solid #e0d5f0;overflow:hidden;box-shadow:0 4px 20px rgba(124,58,237,0.12);">
        <div id="prodMobileAboveUploadPlaceholder" style="width:100%;height:200px;background:linear-gradient(135deg,#f9f7ff 0%,#f0ebff 100%);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;">
          <span style="font-size:40px;line-height:1;">🧍</span>
          <span style="font-size:12px;color:#9a88c0;font-weight:600;">Upload a photo to see live preview</span>
        </div>
      </div>
      <p style="font-size:11px;color:#b0a0c8;margin:8px 0 0;">✏️ Use Erase &amp; Move tools below the preview</p>
    `;
    uploadGroup.insertAdjacentElement('beforebegin', wrapper);
    const showIfMobile = () => { wrapper.style.display = window.innerWidth <= 768 ? 'block' : 'none'; };
    showIfMobile();
    window.addEventListener('resize', showIfMobile);
    const moveRealCanvas = () => {
      if (window.innerWidth > 768) return;
      const slot = dom('prodMobileCanvasSlot'); if (!slot) return;
      const realCanvas = dom('prodStampCanvas-composer') || dom('prodStampCanvas');
      if (!realCanvas || realCanvas.tagName !== 'CANVAS') return;
      const ph = dom('prodMobileAboveUploadPlaceholder'); if (ph) ph.style.display = 'none';
      realCanvas.style.width = '100%'; realCanvas.style.maxWidth = '320px';
      realCanvas.style.display = 'block'; realCanvas.style.margin = '0 auto';
      realCanvas.style.touchAction = 'none';
      slot.appendChild(realCanvas);
      if (typeof window._attachPinchZoomToPhotos === 'function') window._attachPinchZoomToPhotos();
    };
    window._mobileCanvasMoveReady = moveRealCanvas;
  }

  function updateBorderStyle(style) {
    curBorderStyle=style;
    const allClasses = ['border-none','border-simple','border-double','border-dashed','border-dotted','border-double-thin','border-floral','border-floral2','border-floral3','border-thick','border-double-thick','border-dots-lg','border-dash-lg','border-vine','border-corners'];
    Core.getAllCanvases().forEach(c=>{ if(!c) return;
      c.classList.remove(...allClasses);
      if(style && style!=='none') c.classList.add('border-'+style);
      applyBorderVars(c);
    });
    const _showAdvance = () => {
      const show = style !== 'none';
      ['prodBorderSliders','prodBorderWidthGroup'].forEach(id => {
        const el = dom(id); if (el) el.style.display = show ? 'block' : 'none';
      });
      document.querySelectorAll(
        '[data-border-advance],[data-border-sliders],.prod-border-advance,.prod-border-sliders-group'
      ).forEach(el => { el.style.display = show ? 'block' : 'none'; });
    };
    _showAdvance();
    setTimeout(_showAdvance, 250);
    setTimeout(_showAdvance, 700);
  }

  function applyBorderFromSliders() {
    const i=dom('prodBorderInsetSlider'), w=dom('prodBorderWidthSlider');
    if(i) curBorderInset=+i.value; if(w) curBorderWidth=+w.value;
    applyBorderToAll();
  }

  function applyBorderToAll() { Core.getAllCanvases().forEach(c=>{ if(c) applyBorderVars(c); }); }

  function applyBorderVars(c) {
    if(curBorderStyle==='none'){
      ['--border-inset','--border-width'].forEach(p=>c.style.removeProperty(p));
    } else {
      c.style.setProperty('--border-inset', curBorderInset+'px');
      c.style.setProperty('--border-width', curBorderWidth+'px');
    }
  }

  /* ══════════════════════════════════════
     ★ v4.8.0: 上传照片 → 调用 StampCrop.open()
  ══════════════════════════════════════ */
  async function handlePhotoUpload(e) {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = '';
    if (file.size > 5 * 1024 * 1024) { Core.showMessage('error', 'File too large. Max 5MB.'); return; }

    const reader = new FileReader();
    reader.onload = evt => {
      const img = new Image();
      img.onload = () => {
        if (!window.StampCrop) {
          console.error('[StampUI] stamp-crop.js not loaded! Falling back to direct processing.');
          _afterCropConfirm(evt.target.result);
          return;
        }
        StampCrop.open(img, {
          defaultShape: _config.shape === 'square' ? 'square' : 'circle',
          bgMode: _config.photo_processing?.remove_bg_mode || 'full-body',
          onConfirm: async (dataUrl, shapeKey) => {
            _cropShapeKey = shapeKey;
            await _afterCropConfirm(dataUrl);
          },
          onCancel: () => {}
        });
      };
      img.src = evt.target.result;
    };
    reader.readAsDataURL(file);
  }

  /* ══════════════════════════════════════
     ★ v4.8.0: 裁剪完成后的处理管线（原 confirmCrop 去掉 cropToCanvas 部分）
  ══════════════════════════════════════ */
  async function _afterCropConfirm(cropped) {
    Core.showMessage('success', '⏳ Processing photo...');
    try {
      // body-template / curved-text — 用裁剪后的图像走 _handleBodyTemplateUpload 管线
      if (_config.layout === 'body-template' || (_config.layout === 'curved-text' && _config.photo_processing?.enabled)) {
        Object.keys(_styleCache).forEach(k => delete _styleCache[k]); _styleCacheKeys.length = 0;
        const SP = window.StampImageProcessor;
        const hasKey = _creds.removebgKey && _creds.removebgKey !== 'YOUR_REMOVEBG_API_KEY';
        let bgRemoved = cropped;
        if (SP && typeof SP.removeBackground === 'function' && hasKey) {
          Core.showMessage('success', '⏳ Removing background...');
          try { bgRemoved = await SP.removeBackground(cropped); }
          catch(e) { console.warn('[BodyTemplate] BG removal failed, skipping:', e); }
        }
        Core.showMessage('success', '⏳ Converting to line art...');
        const bgImg = await new Promise((res, rej) => {
          const i = new Image(); i.onload=()=>res(i); i.onerror=()=>rej(new Error('Image load failed')); i.src=bgRemoved;
        });
        originalUploadedImage = bgImg;
        let processed = bgRemoved;
        if (SP) {
          const _StickerSP = window.StickerImageProcessor;
          if (_StickerSP && _StickerSP.STYLES && _StickerSP.STYLES.includes(selectedPhotoStyle)) {
            const _stickerImg = await new Promise((res,rej)=>{ const i=new Image(); i.onload=()=>res(i); i.onerror=rej; i.src=bgRemoved; });
            try { processed = await _StickerSP.process(_stickerImg, selectedPhotoStyle); }
            catch(e) { console.warn('[BodyTemplate] Sticker style failed:', e); }
          } else {
            const fn = ({enhanced:SP.convertToEnhancedLineArt,sketch:SP.convertToSketch,silhouette:SP.convertToSilhouette,graphic:SP.convertToGraphic}[selectedPhotoStyle]||SP.convertToLineArt);
            try { processed = await fn.call(SP, bgImg); }
            catch(e) { console.warn('[BodyTemplate] Style conversion failed:', e); }
          }
        }
        if (_config.layout === 'curved-text') {
          Core.updatePhotoDisplay(processed);
          _previewDragOffsetX = 0; _previewDragOffsetY = 0;
          applyPhotoPosition();
          setupPhotoDragOnPreview();
          if (!dom('prodCurvedPhotoTools')) _injectCurvedPhotoUI();
          else { const t = dom('prodCurvedPhotoTools'); if (t) t.style.display = 'block'; }
          _curvedPhotoScale = 1.0;
          _applyCurvedPhotoScale();
        } else {
          if (typeof window.StampBodyComposer !== 'undefined') await window.StampBodyComposer.setHead(processed);
          const tools = dom('prodBodyComposerTools'); if (tools) tools.style.display = 'block';
          const sl = dom('prodHeadSizeSlider'), vl = dom('prodHeadSizeVal');
          if (sl) sl.value = 150; if (vl) vl.textContent = '150%';
          if (window.innerWidth <= 768) {
            if (typeof window._mobileCanvasMoveReady === 'function') {
              window._mobileCanvasMoveReady(); window._mobileCanvasMoveReady = null;
            } else {
              const slot = dom('prodMobileCanvasSlot'), realCanvas = dom('prodStampCanvas-composer') || dom('prodStampCanvas');
              if (slot && realCanvas && !slot.contains(realCanvas)) {
                const ph = dom('prodMobileAboveUploadPlaceholder'); if (ph) ph.style.display='none';
                realCanvas.style.width='100%'; realCanvas.style.display='block'; realCanvas.style.margin='0 auto';
                slot.appendChild(realCanvas);
              }
            }
          }
        }
        if (typeof window._attachPinchZoomToPhotos === 'function') window._attachPinchZoomToPhotos();
        const pu = dom('prodPhotoUploaded'); if (pu) pu.value = 'Yes';
        const purl = dom('prodPhotoUrl'); if (purl) purl.value = processed;
        if (typeof window.StampCollapse !== 'undefined' && typeof window.StampCollapse.refreshStep1 === 'function')
          setTimeout(window.StampCollapse.refreshStep1, 100);
        Core.showMessage('success', '✓ Photo ready! Use Erase to clean edges · Move to reposition');
        return;
      }

      // 标准布局（photo-upload / image-left / image-top 等）
      const img = new Image();
      img.onload = async () => {
        let bgRemoved = cropped;
        if (typeof window.StampImageProcessor !== 'undefined' && typeof window.StampImageProcessor.removeBackground === 'function') {
          Core.showMessage('success', '⏳ Removing background...');
          bgRemoved = await window.StampImageProcessor.removeBackground(cropped, _config.photo_processing?.remove_bg_mode || 'full-body');
        }
        const bgImg = await new Promise((res, rej) => { const i = new Image(); i.onload=()=>res(i); i.onerror=rej; i.src=bgRemoved; });
        originalUploadedImage = bgImg;
        Core.showMessage('success', '⏳ Applying style...');
        let processed = bgRemoved;
        const _StickerSP2 = window.StickerImageProcessor;
        if (_StickerSP2 && _StickerSP2.STYLES && _StickerSP2.STYLES.includes(selectedPhotoStyle)) {
          try { processed = await _StickerSP2.process(bgImg, selectedPhotoStyle); }
          catch(e) { console.warn('[StandardLayout] Sticker style failed:', e); }
        } else if (typeof window.StampImageProcessor !== 'undefined') {
          const sp = window.StampImageProcessor;
          processed = await ({enhanced:sp.convertToEnhancedLineArt,sketch:sp.convertToSketch,silhouette:sp.convertToSilhouette}[selectedPhotoStyle]||sp.convertToLineArt).call(sp, bgImg);
        }
        Core.updatePhotoDisplay(processed);
        if (_config.layout === 'image-left') {
          Core.getAllCanvases().forEach(c => {
            if (!c) return;
            let il = c.querySelector('.prod-illustration-left');
            if (!il) {
              il = document.createElement('div');
              il.className = 'prod-illustration-left prod-illustration-medium';
              c.insertBefore(il, c.firstChild);
            }
            // ★ FIX: 先把 slider 设为 DEFAULT_PHOTO_SIZE，再读值，避免渲染一次 80% 再纠正
            const _psSl = dom('prodPhotoSizeSlider');
            if (_psSl) { _psSl.value = DEFAULT_PHOTO_SIZE; Core.updateSliderFill(_psSl); }
            const _psVal = dom('prodPhotoSizeVal'); if (_psVal) _psVal.textContent = DEFAULT_PHOTO_SIZE + '%';
            const sliderPct = DEFAULT_PHOTO_SIZE;
            const canvasW = c.offsetWidth || 300;
            // slider 直接控制 ilSize，上限是 canvas 宽度的 48%（再大图片会压缩文字空间）
            const ilSize = Math.round(canvasW * Math.min(0.48, sliderPct / 100));
            il.style.cssText = `width:${ilSize}px;height:${ilSize}px;flex-shrink:0;overflow:hidden;display:flex;align-items:center;justify-content:center;cursor:grab;`;
            let img2 = il.querySelector('img.prod-illustration-img');
            if (!img2) {
              img2 = document.createElement('img');
              img2.className = 'prod-illustration-img';
              img2.alt = 'Your photo';
              il.appendChild(img2);
            }
            img2.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;pointer-events:none;';
            img2.src = processed;
          });
          // ★ 立即清除 baseSizePx 缓存并 resize（与 RESET 按钮行为一致），消除滞后感
          _doResetSize();
          setTimeout(() => { setupIllusDragOnPreview(); _doResetSize(); _fitImageLeftText(); }, 150);
      setTimeout(_fitImageLeftText, 600);
        }
        _previewDragOffsetX = 0; _previewDragOffsetY = 0;
        applyPhotoShapeToPreview(); applyPhotoPosition();
        setupPhotoDragOnPreview(); setupTextDragOnPreview();
        if (window.innerWidth <= 768) {
          const slot = dom('prodMobileCanvasSlot'), realCanvas = dom('prodStampCanvas-composer') || dom('prodStampCanvas');
          if (slot && realCanvas && !slot.contains(realCanvas)) {
            const ph = dom('prodMobileAboveUploadPlaceholder'); if (ph) ph.style.display='none';
            realCanvas.style.width='100%'; realCanvas.style.display='block'; realCanvas.style.margin='0 auto';
            slot.appendChild(realCanvas);
          }
        }
        if (typeof window._attachPinchZoomToPhotos === 'function') window._attachPinchZoomToPhotos();
        const pg = dom('prodPhotoPositionGroup');
        if (pg) pg.style.display = window.innerWidth > 768 ? 'none' : 'block';
        const pu = dom('prodPhotoUploaded'); if (pu) pu.value = 'Yes';
        const purl = dom('prodPhotoUrl'); if (purl) purl.value = processed;
        if (typeof window.StampCollapse !== 'undefined' && typeof window.StampCollapse.refreshStep1 === 'function')
          setTimeout(window.StampCollapse.refreshStep1, 100);
        Core.showMessage('success', '✓ Photo processed! Drag image to reposition.');
        if (typeof window._syncMobilePhotoMirror === 'function')
          [600, 1500, 3000].forEach(ms => setTimeout(window._syncMobilePhotoMirror, ms));
      };
      img.src = cropped;
    } catch(err) { Core.showMessage('error', 'Failed to process photo.'); console.error(err); }
  }

  // ★ v4.9: LRU 缓存，最多保留 3 个风格，防止 base64 图片堆积内存
  const _styleCache = {};
  const _styleCacheKeys = [];
  function _setCacheStyle(key, val) {
    if (_styleCacheKeys.includes(key)) {
      _styleCacheKeys.splice(_styleCacheKeys.indexOf(key), 1);
    } else if (_styleCacheKeys.length >= 3) {
      const evict = _styleCacheKeys.shift();
      delete _styleCache[evict];
    }
    _styleCacheKeys.push(key);
    _styleCache[key] = val;
  }

  async function reprocessPhoto(style) {
    if (!originalUploadedImage) return;

    if (_config.layout === 'body-template') {
      Core.showMessage('success','⏳ Processing new style...');
      try {
        const sp = window.StampImageProcessor;
        let processed;
        if (_styleCache[style]) {
          processed = _styleCache[style];
        } else {
          processed = await (async () => {
            const _StickerSP3 = window.StickerImageProcessor;
            if (_StickerSP3 && _StickerSP3.STYLES && _StickerSP3.STYLES.includes(style)) {
              return _StickerSP3.process(originalUploadedImage, style);
            }
            return ({enhanced:sp.convertToEnhancedLineArt,sketch:sp.convertToSketch,silhouette:sp.convertToSilhouette}[style]||sp.convertToLineArt).call(sp, originalUploadedImage);
          })();
          _setCacheStyle(style, processed);
          await window.StampBodyComposer.setHead(processed);
        }
        const purl = dom('prodPhotoUrl'); if (purl) purl.value = processed;
        Core.showMessage('success', `✓ Style: ${style}`);
      } catch(e) { Core.showMessage('error','Failed to change style.'); }
      return;
    }

    function _clearBaseSizePx() {
      Core.getAllCanvases().forEach(c => {
        if (!c) return;
        c.querySelectorAll('.prod-illustration-center,.prod-illustration-left,.prod-illustration-top,.prod-photo-preview,.prod-photo-preview-area')
          .forEach(el => { delete el.dataset.baseSizePx; });
      });
    }
    const _currentSliderVal = +(dom('prodPhotoSizeSlider')?.value || DEFAULT_PHOTO_SIZE);
    if (_styleCache[style]) {
      const processed = _styleCache[style];
      Core.updatePhotoDisplay(processed);
      _refreshErasCanvasPhoto(processed);
      _clearBaseSizePx(); updatePhotoCenterSize(_currentSliderVal);
      applyPhotoShapeToPreview(); applyPhotoPosition();
      const pg=dom('prodPhotoPositionGroup');
      if(pg) pg.style.display=window.innerWidth>768?'none':'block';
      const purl=dom('prodPhotoUrl'); if(purl) purl.value=processed;
      Core.showMessage('success',`✓ Style: ${style}`);
      return;
    }
    Core.showMessage('success','⏳ Processing new style...');
    try {
      const sp=window.StampImageProcessor;
      const processed = await (async () => {
        const _StickerSP4 = window.StickerImageProcessor;
        if (_StickerSP4 && _StickerSP4.STYLES && _StickerSP4.STYLES.includes(style)) {
          return _StickerSP4.process(originalUploadedImage, style);
        }
        return ({enhanced:sp.convertToEnhancedLineArt,sketch:sp.convertToSketch,silhouette:sp.convertToSilhouette}[style]||sp.convertToLineArt).call(sp,originalUploadedImage);
      })();
      _setCacheStyle(style, processed);
      _clearBaseSizePx(); updatePhotoCenterSize(_currentSliderVal);
      applyPhotoShapeToPreview(); applyPhotoPosition();
      const pg=dom('prodPhotoPositionGroup');
      if(pg) pg.style.display=window.innerWidth>768?'none':'block';
      const purl=dom('prodPhotoUrl'); if(purl) purl.value=processed;
      Core.showMessage('success',`✓ Style: ${style}`);
    } catch(e){ Core.showMessage('error','Failed to change style.'); }
  }

  function _refreshErasCanvasPhoto(processed) {
    if (_config.layout !== 'curved-text') return;
    Core.getAllCanvases().forEach(function(c) {
      const ec = c.querySelector('canvas.prod-erase-canvas');
      if (!ec) return;
      const i = new Image(); i.crossOrigin = 'anonymous';
      i.onload = () => { ec._photoImg = i; if (_eraseMode) _redrawEraseCanvas(ec); };
      i.src = processed;
    });
  }

  function openEmailModal() {
    const req=_config.text_lines?.filter(l=>!l.optional)||[];
    for(let i=0;i<req.length;i++){
      const inp=dom(`prodTextLine${i}`);
      if(inp&&!inp.value.trim()){ Core.showMessage('error',`Please enter ${req[i].label}`); inp.focus(); return; }
    }
    if(_config.photo_processing?.enabled && dom('prodPhotoUploaded')?.value!=='Yes'){
      Core.showMessage('error','Please upload a photo first'); return;
    }
    showModalStep('form');
    dom('prodEmailModal')?.classList.add('show');
    setTimeout(()=>dom('prodModalEmail')?.focus(),100);
  }

  function closeEmailModal() {
    dom('prodEmailModal')?.classList.remove('show');
    const me=dom('prodModalEmail'); if(me){ me.classList.remove('error'); me.value=''; }
    dom('prodEmailError')?.classList.remove('show');
    const esb=dom('prodEmailSubmitBtn'); if(esb) esb.disabled=false;
  }

  function showModalStep(step) {
    const sf=dom('prodModalStepForm'), ss=dom('prodModalStepSending'), sr=dom('prodModalStepResult');
    if(sf) sf.style.display=step==='form'?'block':'none';
    ss?.classList.toggle('show',step==='sending');
    sr?.classList.toggle('show',step==='result');
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    const email=dom('prodModalEmail')?.value.trim();
    if(!email||!Core.isValidEmail(email)){ dom('prodModalEmail')?.classList.add('error'); dom('prodEmailError')?.classList.add('show'); return; }
    customerEmailValue=email;
    const ce=dom('prodCustomerEmail'); if(ce) ce.value=email;
    const esb=dom('prodEmailSubmitBtn'); if(esb) esb.disabled=true;
    showModalStep('sending');
    try {
      const st=dom('prodSendingStatusText');
      if(st) st.textContent='Capturing preview...';

      const img = await (async () => {
        if (_config.layout==='body-template' && typeof window.StampBodyComposer!=='undefined')
          return window.StampBodyComposer.getComposedDataUrl();
        return await _buildPreviewImage({});
      })();

      if(!img) throw new Error('Capture failed');
      if(st) st.textContent='Uploading...';
      const url=await Core.uploadToCloudinary(img,_creds.cloudinaryCloudName,_creds.cloudinaryPreset);
      // ★ v4.8.3 FIX: 不覆写 prodPhotoUrl，保留 base64 供 Add to Cart 截图使用
      if(st) st.textContent='Sending email...';
      const tc={};
      _config.text_lines?.forEach((l,i)=>{ const inp=dom(`prodTextLine${i}`); if(inp) tc[l.label]=inp.value.trim(); });
      if(typeof emailjs!=='undefined')
        await emailjs.send(_creds.emailjsServiceId, _creds.emailjsTemplateId, {
          customer_email:email, preview_image:url,
          configuration:`${_config.shape}-${_config.layout}`,
          shape:_config.shape, layout:_config.layout,
          border:_config.border||'none', ink_color:selectedInk,
          stamp_size:selectedSize, text_content:JSON.stringify(tc,null,2),
          order_date:new Date().toLocaleString()
        });
      const ps=dom('prodPreviewSent'); if(ps) ps.value='Yes';
      showResult(true,'Preview Sent!','Your stamp preview has been sent successfully.');
    } catch(err){ showResult(false,'Failed','Error: '+(err.text||err.message||'Unknown error')); }
    const esb2=dom('prodEmailSubmitBtn'); if(esb2) esb2.disabled=false;
  }

  function showResult(ok,title,text) {
    const ri=dom('prodResultIcon'),rt=dom('prodResultTitle'),rx=dom('prodResultText'),rs=dom('prodModalStepResult');
    if(ri) ri.textContent=ok?'✅':'❌';
    if(rt) rt.textContent=title;
    if(rx) rx.textContent=text;
    if(rs) rs.className='prod-send-result show '+(ok?'success':'error');
    showModalStep('result');
  }

  async function _buildPreviewImage(tc) {
    try {
      if (_config.layout === 'body-template' && typeof window.StampBodyComposer !== 'undefined') {
        return window.StampBodyComposer.getComposedDataUrl();
      }

      const layout   = _config.layout || '';
      const shape    = _config.shape  || 'circle';
      const isCircle = shape === 'circle';

      // ── Step 1: 确定参考画布（优先用户实际看到的那个） ──────────────────────
      // 手机端 canvas 可能已被移入 prodMobileCanvasSlot，用 offsetWidth>0 判断可见性
      function _getVisCanvas() {
        for (const id of ['prodMobileCanvasBottom', 'prodStampCanvas', 'prodMobileCanvasTop']) {
          const el = document.getElementById(id);
          if (el && el.offsetWidth > 0 && el.offsetHeight > 0) return el;
        }
        return null;
      }
      const vis  = _getVisCanvas();
      const visW = vis ? vis.offsetWidth  : (window._stampCanvasRefW || 480);
      const visH = vis ? vis.offsetHeight : (window._stampCanvasRefH || 480);

      // 桌面参考尺寸（用于 FS 字号比例），手机时也用缓存的桌面值
      const _dc  = document.getElementById('prodStampCanvas');
      // ★ FIX: 手机端桌面 canvas 被隐藏，offsetWidth/scrollWidth 为 0
      // 此时用 visW 作为参考，保证 FS 与 DOM 实际渲染比例一致
      const _dcWRaw = window._stampCanvasRefW
                   || (_dc && (+_dc.dataset.cachedW || (_dc.offsetWidth > 0 ? _dc.offsetWidth : 0) || _dc.scrollWidth))
                   || 0;
      const _dcW = _dcWRaw > 0 ? _dcWRaw : visW;
      const _dcH = window._stampCanvasRefH
                   || (_dc && (+_dc.dataset.cachedH || _dc.scrollHeight))
                   || Math.round(_dcW * visH / Math.max(visW, 1));

      const TARGET = 1440;
      const aspect = visW / visH;
      const OUT_W  = aspect >= 1 ? TARGET                    : Math.round(TARGET * aspect);
      const OUT_H  = aspect >= 1 ? Math.round(TARGET / aspect) : TARGET;
      // IS: 输出像素 / 可见像素（用于位置/尺寸缩放）
      // FS: 输出像素 / 桌面参考像素（用于字号缩放，手机/桌面保持一致比例）
      const IS = OUT_W / visW;
      const FS = OUT_W / _dcW;
      const cx = OUT_W / 2;
      const cy = OUT_H / 2;

      const canvas = document.createElement('canvas');
      canvas.width  = OUT_W;
      canvas.height = OUT_H;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, OUT_W, OUT_H);

      // ── Step 2: 墨水颜色（从 DOM 实际颜色读取，而非写死 #000000） ─────────
      // INK_COLOR_MAP: { Black:'#000000', Red:'#c0392b', ... }
      const inkColor = (Core.INK_COLOR_MAP && Core.INK_COLOR_MAP[selectedInk]) || '#000000';

      // ── Step 3: 从 DOM 读取每行文字的实际样式（字体/字号/字重/字间距） ────
      // DOM 里 text-line 元素通过 dataset 存储用户调整后的值：
      //   tl.dataset.font / tl.dataset.fontsize / tl.dataset.fontweight / tl.dataset.letterSpacing
      // span 的 computedStyle 是最终显示值，优先级更高
      function _readLineStyle(lineIndex) {
        // 从可见的那个 canvas 读，保证和用户看到的一致
        const refCanvas = vis || _dc;
        if (!refCanvas) return {};
        const tl = refCanvas.querySelector(`[data-line-index="${lineIndex}"]`);
        if (!tl) return {};
        const sp = tl.querySelector('span');
        const cs = sp ? getComputedStyle(sp) : null;

        // 字体：dataset > computedStyle > config 默认
        const font = tl.dataset.font
          || (cs && cs.fontFamily.split(',')[0].replace(/['"]/g, '').trim())
          || (_config.text_lines[lineIndex]?.font)
          || 'Arial';

        // 字号：span inline style > dataset > computedStyle > config 默认
        const fsSrc = (sp && sp.style.fontSize)
          || tl.dataset.fontsize
          || (cs && cs.fontSize)
          || ((_config.text_lines[lineIndex]?.fontSize) + 'px')
          || '22px';
        const fsNum = parseFloat(fsSrc) || 22;

        // 字重：span inline style > dataset > computedStyle > config 默认
        const fw = (sp && sp.style.fontWeight)
          || tl.dataset.fontweight
          || (cs && cs.fontWeight)
          || '400';

        // 字间距：dataset > span inline style > 0
        const ls = parseFloat(tl.dataset.letterSpacing || (sp && sp.style.letterSpacing) || '0') || 0;

        // 文字拖动偏移（已在 _textOffsets 变量中）
        const offAll = _textOffsets['all'] || {x: 0, y: 0};
        const offLine = _textOffsets[lineIndex] || {x: 0, y: 0};
        // image-left 用整体偏移，其他用行偏移
        const off = (layout === 'image-left') ? offAll : offLine;

        return { font, fsNum, fw, ls, offX: off.x, offY: off.y };
      }

      // ── Step 4: 图片尺寸 ─────────────────────────────────────────────────
      const sliderVal = parseFloat(
        (dom('prodPhotoSizeSliderNew') || dom('prodPhotoSizeSlider'))?.value || DEFAULT_PHOTO_SIZE
      );

      // image-left: 直接读DOM里il元素的offsetWidth（现在用直接width/height，不用scale）
      let ilDomSize = 0;
      if (layout === 'image-left' && vis) {
        const ilEl = vis.querySelector('.prod-illustration-left');
        if (ilEl) {
          ilDomSize = ilEl.offsetWidth || 0;
        }
      }

      // curved-text: 固定 72% 直径（与 liquid DOM 一致）
      // image-left: 优先用DOM实际渲染尺寸，回退slider推算值
      // 其他: 用 slider 值 × 可见 canvas 最小边 × IS
      const photoSize = layout === 'curved-text'
        ? Math.round(Math.min(OUT_W, OUT_H) * 0.72)
        : layout === 'image-left'
          ? (ilDomSize > 0
              ? Math.round(ilDomSize * IS)
              // 回退：slider直接控制，上限48%（再大会压缩文字区域）
              : Math.round(visW * Math.min(0.48, sliderVal / 100) * IS))
          : Math.round(Math.min(visW, visH) * sliderVal / 100 * IS);

      // ── Step 5: 找 erase canvas（curved-text 布局） ──────────────────────
      // 手机端用户在 prodMobileCanvasBottom 操作，优先级最高
      let photoSource = null;   // 'eraseCanvas' | 'url' | null
      let eraseCanvasEl = null;
      if (layout === 'curved-text') {
        for (const _cid of ['prodMobileCanvasBottom', 'prodStampCanvas', 'prodMobileCanvasTop']) {
          const _cEl = document.getElementById(_cid);
          if (!_cEl) continue;
          const _pa = _cEl.querySelector('.prod-illustration-center.prod-photo-preview');
          if (!_pa) continue;
          const _ec = _pa.querySelector('canvas.prod-erase-canvas');
          // _eraseMask 存在 = 用户真正擦过，才走 eraseCanvas 路径
          if (_ec && _ec._photoImg && _ec._eraseMask) {
            // 手机端 erase canvas 在 display:none 时 offsetWidth=0，需修正
            if (_ec.width === 0 || _ec.height === 0) {
              const _r  = _pa.getBoundingClientRect();
              const _sz = Math.round(Math.max(_r.width, _r.height, _pa.offsetWidth, _pa.offsetHeight, 360));
              _ec.width = _sz; _ec.height = _sz;
              if (_ec._eraseMask) { _ec._eraseMask.width = _sz; _ec._eraseMask.height = _sz; }
            }
            _redrawEraseCanvas(_ec);  // 同步最新 drag+zoom+mask
            eraseCanvasEl = _ec;
            photoSource = 'eraseCanvas';
            break;
          }
        }
      }
      const photoB64 = dom('prodPhotoUrl')?.value;
      if (!photoSource && photoB64 && photoB64.startsWith('data:')) photoSource = 'url';

      // ── Step 5.5: 先画边框（在图片之下，与DOM层叠顺序一致） ─────────────
      {
        const bs = curBorderStyle || 'none';
        if (bs !== 'none') {
          const inset = Math.round((curBorderInset || 10) * IS);
          const bw    = Math.max(1, Math.round((curBorderWidth || 2) * IS));
          ctx.strokeStyle = inkColor;
          const drawLine = (ins, lw, dash) => {
            ctx.lineWidth = lw;
            ctx.setLineDash(dash || []);
            ctx.lineCap = (dash && dash[0] <= 4) ? 'round' : 'butt';
            if (isCircle && OUT_W === OUT_H) {
              ctx.beginPath();
              ctx.arc(cx, cy, Math.max(4, OUT_W / 2 - ins), 0, Math.PI * 2);
              ctx.stroke();
            } else {
              ctx.strokeRect(ins, ins, OUT_W - ins * 2, OUT_H - ins * 2);
            }
            ctx.setLineDash([]);
            ctx.lineCap = 'butt';
          };
          if (bs === 'simple') {
            drawLine(inset, bw);
          } else if (bs === 'thick') {
            drawLine(inset, Math.round(bw * 2.5));
          } else if (bs === 'dashed' || bs === 'dash-lg') {
            const dl = Math.round((bs === 'dash-lg' ? 20 : 12) * IS);
            drawLine(inset, bw, [dl, Math.round(dl * 0.5)]);
          } else if (bs === 'dotted' || bs === 'dots-lg') {
            const dp = Math.round((bs === 'dots-lg' ? 8 : 4) * IS);
            drawLine(inset, bw, [dp, dp]);
          } else {
            const gap = Math.round(5 * IS);
            drawLine(inset, bw);
            drawLine(inset + gap + bw, Math.max(1, Math.round(bw * 0.6)));
          }
        }
      }

      // ── Step 6: 绘制图片 ──────────────────────────────────────────────────
      if (photoSource) {
        await new Promise(resolve => {
          const drawPhoto = (imgEl) => {
            ctx.save();
            if (layout === 'image-left') {
              const domPadL = vis ? parseFloat(getComputedStyle(vis).paddingLeft  || '22') : 22;
              const domPadT = vis ? parseFloat(getComputedStyle(vis).paddingTop   || '20') : 20;
              const domPadB = vis ? parseFloat(getComputedStyle(vis).paddingBottom|| '20') : 20;

              // il现在用直接width/height，il.offsetHeight = 实际显示高度
              const ilEl    = vis?.querySelector('.prod-illustration-left');
              const ilH     = (ilEl?.offsetHeight || photoSize);
              const contentH = OUT_H - Math.round((domPadT + domPadB) * IS);
              // il在canvas内容区垂直居中（flex align-items:center）
              const ilTop   = Math.round(domPadT * IS) + (contentH - ilH * IS) / 2;

              // 应用 object-fit:contain：按比例缩放
              const iw = imgEl.naturalWidth  || imgEl.width  || photoSize;
              const ih = imgEl.naturalHeight || imgEl.height || photoSize;
              const drawSize = photoSize; // photoSize = il.offsetWidth * IS
              const scale = Math.min(drawSize / iw, drawSize / ih);
              const dw = iw * scale, dh = ih * scale;

              const basePx = Math.round(domPadL * IS);
              // 在il区域内水平/垂直居中
              const px = basePx + Math.round((_illusDragOffsetX || 0) * IS) + (drawSize - dw) / 2;
              const py = ilTop  + Math.round((_illusDragOffsetY || 0) * IS) + (drawSize * ilH / photoSize - dh) / 2+Math.round(45 * IS);
              ctx.drawImage(imgEl, px, py, dw, dh);
            } else if (layout === 'image-top') {
              const basePx = (OUT_W - photoSize) / 2;
              const basePy = Math.round(0.04 * OUT_H);
              // image-top 也应用 _previewDragOffsetX/Y
              const px = basePx + Math.round((_previewDragOffsetX || 0) * IS);
              const py = basePy + Math.round((_previewDragOffsetY || 0) * IS);
              ctx.drawImage(imgEl, px, py, photoSize, photoSize);
            } else {
              // curved-text / photo-upload / 其他居中圆形裁切
              ctx.beginPath();
              ctx.arc(cx, cy, photoSize / 2, 0, Math.PI * 2);
              ctx.clip();
              if (photoSource === 'eraseCanvas') {
                // erase canvas 已含 drag+zoom+mask，直接映射到输出区域
                ctx.drawImage(imgEl, 0, 0, imgEl.width, imgEl.height,
                  cx - photoSize / 2, cy - photoSize / 2, photoSize, photoSize);
              } else {
                // base64 图片：手动应用 drag offset + curved zoom
                const iw = imgEl.naturalWidth  || imgEl.width  || photoSize;
                const ih = imgEl.naturalHeight || imgEl.height || photoSize;
                const fitSc = Math.min(photoSize / iw, photoSize / ih);
                const sc    = fitSc * (_curvedPhotoScale || 1.0);
                const drawX = cx + (_previewDragOffsetX || 0) * IS - (iw * sc) / 2;
                const drawY = cy + (_previewDragOffsetY || 0) * IS - (ih * sc) / 2;
                ctx.drawImage(imgEl, drawX, drawY, iw * sc, ih * sc);
              }
            }
            ctx.restore();
            resolve();
          };

          if (photoSource === 'eraseCanvas') {
            drawPhoto(eraseCanvasEl);
          } else {
            const img = new Image();
            img.onload  = () => drawPhoto(img);
            img.onerror = resolve;
            img.src     = photoB64;
          }
        });
      }

      // ── Step 7: curved-text overlay（镂空 PNG） ───────────────────────────
      if (layout === 'curved-text' && _config.illustration && _config.illustration.url) {
        await new Promise(resolve => {
          const _ov = new Image();
          _ov.crossOrigin = 'anonymous';
          _ov.onload = () => {
            ctx.drawImage(_ov, cx - photoSize / 2, cy - photoSize / 2, photoSize, photoSize);
            resolve();
          };
          _ov.onerror = resolve;
          _ov.src = _config.illustration.url;
        });
      }

      // ── Step 8: 绘制文字（从 DOM 读取真实字体/字号/字重/字间距/拖动偏移） ──
      const lines = _config.text_lines || [];

      // 辅助：构建 ctx.font 字符串
      function _ctxFont(fw, fsPx, fontName) {
        return `${fw} ${fsPx}px "${fontName}", sans-serif`;
      }

      if (layout === 'curved-text') {
        const outerR = Math.min(OUT_W, OUT_H) / 2;
        // 边框占用（inset + lineWidth + 安全边距）
        const _hasBdr   = curBorderStyle && curBorderStyle !== 'none';
        const _bdrClear = _hasBdr
          ? Math.round((curBorderInset || 10) * IS) + Math.max(1, Math.round((curBorderWidth || 2) * IS)) + Math.round(4 * IS)
          : Math.round(4 * IS);

        lines.forEach((lc, i) => {
          const text = dom(`prodTextLine${i}`)?.value.trim() || '';
          if (!text) return;
          const { font, fsNum, fw, ls } = _readLineStyle(i);
          const fs  = Math.round(fsNum * FS);
          const pos = lc.position || (i === 0 ? 'top-curve' : 'bottom-curve');
          const isBottom = (pos === 'bottom-curve' || pos === 'bottom');
          // radiusScale 已在 init 时被修正（bottom-curve 上限 0.92），直接读取
          const rScale = lc.radiusScale || 1.0;

          let arcR;
          if (isBottom) {
            // 下弧：从图片半径往外一个字高，radiusScale 控制偏移量
            // 与 DOM 渲染一致：radiusScale=0.92 时文字在图片边缘偏内侧
            const baseBottom = photoSize / 2 + fs + Math.round(6 * IS);
            arcR = baseBottom * rScale;
            arcR = Math.min(arcR, outerR - _bdrClear - fs / 2); // 不超边框
          } else {
            // 上弧：从外圆内侧，radiusScale 控制距边框的距离
            arcR = (outerR - _bdrClear - fs / 2) * rScale;
          }
          arcR = Math.max(arcR, photoSize / 2 + Math.round(4 * IS));
          arcR = Math.max(arcR, fs * 1.5);

          ctx.font         = _ctxFont(fw, fs, font);
          ctx.fillStyle    = inkColor;
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'middle';
          const chars    = text.split('');
          const lsVal    = ls * FS;
          if ('letterSpacing' in ctx) ctx.letterSpacing = lsVal + 'px';
          const cw       = chars.map(ch => ctx.measureText(ch).width + (lsVal || 0));
          const totalRad = cw.reduce((a, b) => a + b, 0) / arcR;
          const half     = totalRad / 2;
          function drawArc(arr, ws, center, sign) {
            let cur = center - half;
            arr.forEach((ch, j) => {
              const mid = cur + ws[j] / 2 / arcR;
              ctx.save();
              ctx.translate(cx + arcR * Math.cos(mid), cy + arcR * Math.sin(mid));
              ctx.rotate(mid + sign * Math.PI / 2);
              ctx.fillText(ch, 0, 0);
              ctx.restore();
              cur += ws[j] / arcR;
            });
          }
          if (!isBottom) drawArc(chars, cw, -Math.PI / 2, 1);
          else           drawArc([...chars].reverse(), [...cw].reverse(), Math.PI / 2, -1);
          if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
        });

      } else if (layout === 'image-left') {
        // image-left：文字在图片右侧，整体可拖动（_textOffsets['all']）
        const domPadL2  = vis ? parseFloat(getComputedStyle(vis).paddingLeft  || '22') : 22;
        const domPadT2  = vis ? parseFloat(getComputedStyle(vis).paddingTop   || '20') : 20;
        const domPadB2  = vis ? parseFloat(getComputedStyle(vis).paddingBottom|| '20') : 20;
        const domGap    = vis ? parseFloat(getComputedStyle(vis).gap || '16') : 16;
        const contentH2 = OUT_H - Math.round((domPadT2 + domPadB2) * IS);
        // ★ FIX: drawSize2 用图片实际宽度（photoSize），不受高度限制
        // 之前 Math.min(photoSize, contentH2) 用高度截断了宽度，导致 canvas 里图片偏窄，
        // textX 偏左，视觉上图片和文字间距比 DOM 大
        const drawSize2 = photoSize;
        // ★ FIX: illusDragOffsetX 是 CSS transform，只做视觉偏移，不影响 flex 布局里文字的起始位置
        // textX 不应该加 illusDragOffsetX
        const imgBasePx = Math.round(domPadL2 * IS);
        const textX     = imgBasePx + drawSize2 + Math.round(domGap * IS);
        const maxTextW  = OUT_W - textX - Math.round(domPadL2 * IS);

        // 文字容器整体偏移（来自 _textOffsets['all']）
        const containerOff = _textOffsets['all'] || {x: 0, y: 0};
        const containerOffX = Math.round((containerOff.x || 0) * IS);
        const containerOffY = Math.round((containerOff.y || 0) * IS);

        const lds = lines.map((lc, i) => {
          const text = dom(`prodTextLine${i}`)?.value.trim() || '';
          const { font, fsNum, fw, ls } = _readLineStyle(i);
          let fs = Math.round(fsNum * FS);
          // 超宽时缩小字号（与 DOM 自动缩小逻辑一致）
          ctx.font = _ctxFont(fw, fs, font);
          const minFs = Math.round(8 * FS);
          while (fs > minFs && ctx.measureText(text).width > maxTextW) {
            fs -= Math.max(1, Math.round(FS * 0.5));
            ctx.font = _ctxFont(fw, fs, font);
          }
          return { text, fs, font, fw, ls, i };
        }).filter(d => d.text);

        const lg   = Math.round(6 * FS);
        const totH = lds.reduce((s, d) => s + d.fs + lg, 0) - lg;
        // DOM 里文字容器是 flex:1 align-items:center → 垂直居中于内容区
        const padT  = Math.round(domPadT2 * IS);
        const padB  = Math.round(domPadB2 * IS);
        const ty0   = padT + Math.max(0, (OUT_H - padT - padB - totH) / 2);
        let   ty    = ty0 + containerOffY;
        const tx    = textX + containerOffX;

        lds.forEach(({ text, fs, font, fw, ls }) => {
          ctx.font = _ctxFont(fw, fs, font);
          if ('letterSpacing' in ctx) ctx.letterSpacing = (ls * FS) + 'px';
          ctx.fillStyle    = inkColor;
          ctx.textAlign    = 'left';
          ctx.textBaseline = 'top';
          ctx.fillText(text, tx, ty);
          ty += fs + lg;
        });
        if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';

      } else {
        // photo-upload / text-only / image-top 等标准居中布局
        const hasPhoto  = !!(photoB64 && photoB64.startsWith('data:'));
        const hasBorder = curBorderStyle && curBorderStyle !== 'none';
        const INNER_PAD = Math.round((hasBorder
          ? (curBorderInset || 10) + (curBorderWidth || 2) + 10
          : 10) * IS);
        const maxTextW = OUT_W - INNER_PAD * 2;
        const availH   = OUT_H - INNER_PAD * 2;

        const lds2 = lines.map((lc, i) => {
          const text = dom(`prodTextLine${i}`)?.value.trim() || '';
          const { font, fsNum, fw, ls, offX, offY } = _readLineStyle(i);
          let fs = Math.round(fsNum * FS);
          if (text) {
            ctx.font = _ctxFont(fw, fs, font);
            while (fs > 12 && ctx.measureText(text).width > maxTextW) {
              fs = Math.max(12, fs - 2);
              ctx.font = _ctxFont(fw, fs, font);
            }
          }
          return { text, fs, font, fw, ls, offX, offY, i };
        }).filter(d => d.text);

        const lg2  = Math.round(6 * IS);
        let totH2  = lds2.reduce((s, d) => s + d.fs + lg2, 0) - lg2;

        // 仅 text-only（无图片）时尝试放大字号以填充画布
        if (!hasPhoto && lds2.length > 0 && totH2 > 0) {
          const TARGET_FILL = 0.65;
          const fillRatio   = totH2 / availH;
          if (fillRatio < TARGET_FILL) {
            const scaleUp = Math.min(TARGET_FILL / fillRatio, 2.5);
            let allFit = true;
            lds2.forEach(d => {
              const newFs = Math.round(d.fs * scaleUp);
              if (d.text) {
                ctx.font = _ctxFont(d.fw, newFs, d.font);
                if (ctx.measureText(d.text).width > maxTextW * 1.1) allFit = false;
              }
              d.fs = newFs;
            });
            if (!allFit) lds2.forEach(d => { d.fs = Math.round((parseFloat(d.lc?.fontSize) || 22) * FS); });
            totH2 = lds2.reduce((s, d) => s + d.fs + lg2, 0) - lg2;
          }
        }

        const tyMin = INNER_PAD;
        const tyMax = OUT_H - totH2 - INNER_PAD;
        // 有图片时文字放下方；无图片时垂直居中
        const tyBase = hasPhoto
          ? Math.min(Math.max(tyMin, Math.round(OUT_H * 0.76)), tyMax)
          : Math.max(tyMin, Math.round((OUT_H - totH2) / 2));

        let ty = tyBase;
        lds2.forEach(({ text, fs, font, fw, ls, offX, offY }) => {
          ctx.font = _ctxFont(fw, fs, font);
          if ('letterSpacing' in ctx) ctx.letterSpacing = (ls * FS) + 'px';
          ctx.fillStyle    = inkColor;
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'top';
          // 应用每行独立拖动偏移（offX/offY 已是 DOM 像素，需 × IS 换算到输出像素）
          ctx.fillText(text, cx + Math.round(offX * IS), ty + Math.round(offY * IS));
          ty += fs + lg2;
        });
        if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
      }

      // ── Step 9: 边框已在 Step 5.5 绘制（图片之下），此处不重复 ───────────

      return canvas.toDataURL('image/png');
    } catch (err) {
      console.warn('_buildPreviewImage failed:', err);
      return null;
    }
  }

  let _submitting = false;
  async function handleFormSubmit(e) {
    e.preventDefault();
    if (_submitting) return;
    dom('prodErrorMessage')?.classList.remove('show');
    dom('prodSuccessMessage')?.classList.remove('show');
    const req=_config.text_lines?.filter(l=>!l.optional)||[];
    for(let i=0;i<req.length;i++){
      const inp=dom(`prodTextLine${i}`);
      if(inp&&!inp.value.trim()){ Core.showMessage('error',`Please enter ${req[i].label}`); inp.focus(); return; }
    }

    if(_config.photo_processing?.enabled && dom('prodPhotoUploaded')?.value!=='Yes'){
      if (!document.getElementById('_noPhotoStyle')) {
        const st = document.createElement('style');
        st.id = '_noPhotoStyle';
        st.textContent = `
          #_noPhotoToast { position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.55);z-index:99999;display:flex;align-items:center;justify-content:center;animation:_npFadeIn 0.2s ease; }
          @keyframes _npFadeIn { from{opacity:0} to{opacity:1} }
          #_noPhotoToast .np-box { background:#fff;border-radius:20px;padding:36px 32px 28px;max-width:320px;width:88%;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,0.3);animation:_npPop 0.25s cubic-bezier(0.34,1.56,0.64,1); }
          @keyframes _npPop { from{transform:scale(0.8);opacity:0} to{transform:scale(1);opacity:1} }
          #_noPhotoToast .np-icon { font-size:56px;line-height:1;margin-bottom:12px; }
          #_noPhotoToast .np-title { font-size:20px;font-weight:800;color:#e53e3e;margin-bottom:8px; }
          #_noPhotoToast .np-sub { font-size:14px;color:#666;margin-bottom:24px;line-height:1.5; }
          #_noPhotoToast .np-btn { display:inline-block;background:linear-gradient(135deg,#e53e3e,#c53030);color:#fff;border:none;border-radius:50px;padding:13px 36px;font-size:15px;font-weight:700;cursor:pointer;width:100%;letter-spacing:0.3px; }
          #_noPhotoToast .np-btn:hover { opacity:0.9; }
        `;
        document.head.appendChild(st);
      }
      const overlay = document.createElement('div');
      overlay.id = '_noPhotoToast';
      overlay.innerHTML = `<div class="np-box"><div class="np-icon">📸</div><div class="np-title">No Photo Uploaded!</div><div class="np-sub">Please upload a photo before<br>adding to cart.</div><button class="np-btn" id="_noPhotoBtn">Upload Photo Now</button></div>`;
      (document.getElementById('_reviewModal') || document.body).appendChild(overlay);
      const dismiss = () => {
        overlay.style.transition = 'opacity 0.15s'; overlay.style.opacity = '0';
        setTimeout(() => overlay.remove(), 160);
        const uploadArea = document.querySelector('.prod-file-input-wrapper') || document.querySelector('.prod-file-input');
        if (uploadArea) { uploadArea.style.transition='box-shadow 0.3s'; uploadArea.style.boxShadow='0 0 0 3px #e53e3e'; setTimeout(()=>{uploadArea.style.boxShadow='';},3000); }
        const scrollTarget = document.querySelector('.prod-file-input-row') || document.querySelector('.prod-file-input-wrapper') || document.querySelector('#prodPhotoUpload');
        if (scrollTarget) scrollTarget.scrollIntoView({ behavior:'smooth', block:'center' });
        if (typeof window.StampCollapse !== 'undefined' && typeof window.StampCollapse.openStep === 'function') window.StampCollapse.openStep(1);
      };
      document.getElementById('_noPhotoBtn').addEventListener('click', dismiss);
      overlay.addEventListener('click', e => { if (e.target === overlay) dismiss(); });
      return;
    }

    _submitting = true;
    const btn=dom('prodSubmitBtn'), stickyBtn=dom('prodStickyBarBtn');
    function _setBtnState(disabled, text, opacity) {
      btn.disabled=disabled; btn.textContent=text; btn.style.opacity=opacity;
      if (stickyBtn) { stickyBtn.disabled=disabled; stickyBtn.textContent=text; stickyBtn.style.opacity=opacity; }
    }
    _setBtnState(true, 'Processing...', '0.7');
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      const tc = {};
      _config.text_lines?.forEach((l,i)=>{ const inp=dom(`prodTextLine${i}`); if(inp) tc[l.label]=inp.value.trim(); });

      let previewUrl = '';
      try {
        const imgData = await _buildPreviewImage(tc);
        if (imgData) previewUrl = await Core.uploadToCloudinary(imgData, _creds.cloudinaryCloudName, _creds.cloudinaryPreset);
      } catch(captureErr) { console.warn('Preview capture skipped:', captureErr); }

      document.querySelectorAll('input[data-cart-text-line]').forEach(el => el.remove());
      const form = dom('prodStampForm');
      if (form) {
        Object.entries(tc).forEach(([label, value]) => {
          if (!value) return;
          const inp = document.createElement('input');
          inp.type = 'hidden'; inp.name = `properties[${label}]`; inp.value = value;
          inp.setAttribute('data-cart-text-line', '1');
          form.appendChild(inp);
        });
      }

      const cartProperties = {};
      dom('prodStampForm')?.querySelectorAll('input[type="hidden"]').forEach(inp => {
        const m = inp.name && inp.name.match(/^properties\[(.+)\]$/);
        if (m) cartProperties[m[1]] = inp.value;
      });
      Object.entries(tc).forEach(([label, value]) => { if (value) cartProperties[label] = value; });
      if (previewUrl) cartProperties['_Photo URL'] = previewUrl;

      const variantId = dom('prodVariantId')?.value || new FormData(dom('prodStampForm')).get('id');
      const cartData = { id: variantId, quantity: 1, properties: cartProperties };

      // ★ v4.9: 加超时保护（15s），防止网络慢时按钮永久卡死
      const _cartController = new AbortController();
      const _cartTimeout = setTimeout(() => _cartController.abort(), 15000);
      let res;
      try {
        res = await fetch('/cart/add.js', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(cartData), signal: _cartController.signal });
      } finally {
        clearTimeout(_cartTimeout);
      }
      if (!res.ok) { const er = await res.json().catch(() => ({})); throw new Error(er.description || 'Failed to add to cart'); }
      Core.showMessage('success', '✓ Added to cart! Redirecting...');
      try {
        const cr = await fetch('/cart.js'); const cart = await cr.json();
        document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = cart.item_count);
      } catch(e) {}

      // ★ v4.8.3 FIX: 只在用户没有点过 Send Preview 时才发卖家邮件，避免重复
      const _alreadySent = dom('prodPreviewSent')?.value === 'Yes';
      if (!_alreadySent && typeof emailjs !== 'undefined' && _creds.sellerEmail && _creds.sellerEmail !== 'your-seller-email@example.com') {
        emailjs.send(_creds.emailjsServiceId, _creds.emailjsTemplateId, {
          to_email: _creds.sellerEmail, customer_email: customerEmailValue || dom('prodCustomerEmail')?.value || 'Not provided',
          preview_image: previewUrl || 'N/A', preview_url: previewUrl || 'N/A',
          configuration: `${_config.shape}-${_config.layout}`, text_content: JSON.stringify(tc, null, 2),
          ink_color: selectedInk || 'N/A', stamp_size: selectedSize || 'N/A',
          border_style: _config.border || 'none', order_date: new Date().toLocaleString()
        }).catch(err => console.warn('Seller email skipped:', err));
      }

      setTimeout(() => window.location.href = '/cart', 1500);
    } catch(err) {
      _submitting = false;
      Core.showMessage('error', 'Failed to add to cart. Please try again.');
      _setBtnState(false, '🛒 Add to Cart', '1');
    }
  }

  function _fitTextOnlyCanvas() {
    if (!_config || _config.layout !== 'text-only') return;
    var H_PAD = 32;
    var MIN_W = 160, MAX_W = 520;
    Core.getAllCanvases().forEach(function(c) {
      if (!c) return;
      c.style.width = 'max-content';
      var maxW = 0;
      c.querySelectorAll('.prod-text-line span').forEach(function(sp) {
        var prev = sp.style.whiteSpace;
        sp.style.whiteSpace = 'nowrap';
        var w = sp.scrollWidth || sp.getBoundingClientRect().width;
        sp.style.whiteSpace = prev;
        if (w > maxW) maxW = w;
      });
      var targetW = Math.min(MAX_W, Math.max(MIN_W, Math.ceil(maxW) + H_PAD * 2));
      c.style.width = targetW + 'px';
    });
    _updateDomDebugPanel();
  }

  function _fitImageLeftText() {
    if (_config.layout !== 'image-left') return;
    var isMobile = window.innerWidth <= 768;
    Core.getAllCanvases().forEach(function(c) {
      if (!c) return;
      if (isMobile) {
        var illus = c.querySelector('.prod-illustration-left');
        if (illus) {
          var illusPx = window.innerWidth <= 480 ? 110 : 140;
          illus.style.cssText = illus.style.cssText + ';width:'+illusPx+'px!important;height:'+illusPx+'px!important;max-width:'+illusPx+'px!important;max-height:'+illusPx+'px!important;flex-shrink:0!important';
        }
        c.style.gap = '16px';
        // 手机端不缩画布宽度
      }
      var container = c.querySelector('.prod-text-container');
      if (!container) return;
      var containerW = container.getBoundingClientRect().width;
      if (isMobile && (!containerW || containerW < 30)) {
        var canvasW = c.getBoundingClientRect().width || c.offsetWidth || 320;
        var padding = 32, illusPxFallback = window.innerWidth <= 480 ? 110 : 140;
        var illusW = illusPxFallback + 16;
        containerW = canvasW - padding - illusW;
      }
      if (!containerW || containerW < 20) return;
      var tl0 = c.querySelector('[data-line-index="0"]'), sp0 = tl0 && tl0.querySelector('span');
      if (sp0) {
        var origFs = parseFloat((_config.text_lines[0] && _config.text_lines[0].fontSize) || '30px') || 30;
        sp0.style.fontSize = origFs + 'px';
        if (isMobile) sp0.style.fontWeight = '700';
        sp0.style.whiteSpace = 'nowrap';
        var fs = origFs, att = 0;
        while (sp0.scrollWidth > containerW + 1 && fs > (isMobile ? 16 : 11) && att < 80) { fs -= 0.5; sp0.style.fontSize = fs + 'px'; att++; }
        sp0.style.whiteSpace = 'normal';
      }
      var namePx = sp0 ? parseFloat(sp0.style.fontSize) : 30;
      var addrOrig = parseFloat((_config.text_lines[1] && _config.text_lines[1].fontSize) || '18px') || 18;
      var maxAddr = Math.min(addrOrig, namePx * 0.68);
      [1, 2, 3].forEach(function(i) {
        var tli = c.querySelector('[data-line-index="'+i+'"]'), sp = tli && tli.querySelector('span');
        if (!sp) return;
        sp.style.fontSize = maxAddr + 'px'; sp.style.whiteSpace = 'nowrap';
        var fs3 = maxAddr, att2 = 0;
        while (sp.scrollWidth > containerW + 1 && fs3 > 10 && att2 < 30) { fs3 -= 0.5; sp.style.fontSize = fs3 + 'px'; att2++; }
        sp.style.whiteSpace = 'normal';
      });

      // ★ FIX: 桌面端根据文字实际宽度收缩画布，消除右侧多余空白
      if (!isMobile) {
        // 1. 量出所有文字行中最宽的实际渲染宽度
        var maxTextW = 0;
        container.querySelectorAll('.prod-text-line span').forEach(function(sp2) {
          var prev = sp2.style.whiteSpace;
          sp2.style.whiteSpace = 'nowrap';
          var w = sp2.scrollWidth || sp2.getBoundingClientRect().width;
          sp2.style.whiteSpace = prev;
          if (w > maxTextW) maxTextW = w;
        });
        if (maxTextW < 10) return; // 还没有文字，跳过

        // 2. 计算各部分宽度
        var cStyle   = getComputedStyle(c);
        var padL     = parseFloat(cStyle.paddingLeft  || '0');
        var padR     = parseFloat(cStyle.paddingRight || '0');
        var gap      = parseFloat(cStyle.gap || cStyle.columnGap || '16');
        var illusEl  = c.querySelector('.prod-illustration-left');
        var illusW   = illusEl ? (illusEl.offsetWidth || 0) : 0;
        var MIN_W    = 200; // 最小画布宽度，防止过窄

        // 3. 目标宽度 = 左padding + 图片 + gap + 文字实际宽 + 右padding + 少量呼吸空间
        var targetW = Math.max(MIN_W, Math.ceil(padL + illusW + gap + maxTextW + padR + 60));

        // 4. 只缩不扩（用户看了之后加字才会扩，不要无故变宽）
        var currentW = c.offsetWidth;
        if (targetW < currentW) {
          c.style.width    = targetW + 'px';
          c.style.maxWidth = targetW + 'px';
          c.style.minWidth = MIN_W   + 'px';
        }
      }
    });
    _updateDomDebugPanel();
  }

  function _updateDomDebugPanel() {
    if (!_config) return;
    var panel = document.getElementById('prodDomDebugInfo');
    var body;
    if (!panel) {
      var anchor = document.querySelector('.prod-preview-section');
      if (!anchor) return;
      panel = document.createElement('div');
      panel.id = 'prodDomDebugInfo';
      panel.style.cssText = 'margin-top:8px;border-radius:8px;overflow:hidden;border:1px solid #e0d5f0;font-size:11px;user-select:none;';
      var hdr = document.createElement('div');
      hdr.style.cssText = 'background:#f0ebff;padding:5px 10px;cursor:pointer;display:flex;justify-content:space-between;align-items:center;';
      hdr.innerHTML = '<span style="font-size:10px;font-weight:700;color:#6c3fc5;letter-spacing:.8px">DEBUG INFO</span><span id="prodDomDbgArrow" style="font-size:10px;color:#6c3fc5">&#9658; dblclick to expand</span>';
      body = document.createElement('div');
      body.id = 'prodDomDebugInfoBody';
      body.style.display = 'none'; body.style.background = '#fdfcff';
      panel.appendChild(hdr); panel.appendChild(body);
      var _dbgOpen = false;
      hdr.addEventListener('dblclick', function() {
        _dbgOpen = !_dbgOpen; body.style.display = _dbgOpen ? 'block' : 'none';
        var a = document.getElementById('prodDomDbgArrow');
        if (a) a.innerHTML = _dbgOpen ? '&#9660; dblclick to collapse' : '&#9658; dblclick to expand';
      });
      anchor.appendChild(panel);
    } else { body = document.getElementById('prodDomDebugInfoBody'); }
    if (!body) return;
    var c = document.getElementById('prodStampCanvas'); if (!c) return;
    var cR = c.getBoundingClientRect();
    // ★ FIX: detect photo element (curved-text / photo-upload / image-left etc.)
    var il = c.querySelector('.prod-photo-preview-area, .prod-illustration-center.prod-photo-preview')
          || c.querySelector('.prod-illustration-left, .prod-illustration-top, .prod-illustration-center');
    var iR = il ? il.getBoundingClientRect() : null;
    // photo img inside the area (for transform-based size)
    var photoImg = il ? il.querySelector('img.prod-uploaded-photo, img.prod-illustration-img') : null;
    var tb = c.querySelector('.prod-text-container'), tR = tb ? tb.getBoundingClientRect() : null;
    // ★ FIX v4.8.4: canvas 有 CSS padding，子元素的 getBoundingClientRect().top
    // 从 padding 内侧算起，若 oy 只取 cR.top（外边缘），显示值会比实际渲染 Y 偏大 ~30px
    // 加上 paddingLeft/paddingTop 使 DEBUG 坐标系与 _buildPreviewImage 绘制坐标系对齐
    var _cStyle = getComputedStyle(c);
    var ox = cR.left + parseFloat(_cStyle.paddingLeft  || '0');
    var oy = cR.top  + parseFloat(_cStyle.paddingTop   || '0');
    var f = function(v){ return v!==null&&v!==undefined ? Math.round(v) : '-'; };
    var r = function(a,b){ return a ? Math.round(a-b) : '-'; };
    var H = function(e,l){ return '<tr style="background:#f0ebff"><td colspan="2" style="padding:5px 8px;font-weight:700;color:#6c3fc5;font-family:sans-serif;font-size:10px;letter-spacing:.8px">'+e+' '+l+'</td></tr>'; };
    var D = function(l,v,a){ return '<tr'+(a?' style="background:#faf9ff"':'')+'><td style="padding:3px 8px;color:#888;white-space:nowrap">'+l+'</td><td style="padding:3px 8px;font-weight:600;color:#1a1a1a">'+v+'</td></tr>'; };
    var canvasClasses = Array.from(c.classList).join('  ') || '(none)';
    var par = c.parentElement;
    var parHtml = '';
    if (par) {
      var ps = getComputedStyle(par);
      var parClasses = Array.from(par.classList).join('  ') || '(none)';
      var px = function(v){ return v === '0px' ? '0' : v; };
      var mg = px(ps.marginTop)+' '+px(ps.marginRight)+' '+px(ps.marginBottom)+' '+px(ps.marginLeft);
      var pd = px(ps.paddingTop)+' '+px(ps.paddingRight)+' '+px(ps.paddingBottom)+' '+px(ps.paddingLeft);
      parHtml = H('Parent &lt;' + par.tagName.toLowerCase() + '&gt;', 'BOX MODEL')
        + D('Classes',    parClasses,    false)
        + D('Margin',     mg,            true)
        + D('Padding',    pd,            false)
        + D('Box-sizing', ps.boxSizing,  true);
    }

    // ★ FIX: photo size from slider, scale from CSS transform, drag offset from _previewDragOffsetX/Y
    var photoSlider = dom('prodPhotoSizeSliderNew') || dom('prodPhotoSizeSlider') || dom('prodCurvedSizeSlider');
    var photoSizePct = photoSlider ? photoSlider.value + '%' : '-';
    // parse scale from img transform if available
    var photoScale = '-';
    if (photoImg && photoImg.style.transform) {
      var scaleMatch = photoImg.style.transform.match(/scale\(([\d.]+)\)/);
      if (scaleMatch) photoScale = parseFloat(scaleMatch[1]).toFixed(3);
    }
    var isCurved = _config.layout === 'curved-text';
    var dragOffsetStr = isCurved
      ? 'x:'+Math.round(_previewDragOffsetX||0)+'  y:'+Math.round(_previewDragOffsetY||0)
        + '  zoom:'+(_curvedPhotoScale||1).toFixed(2)
      : 'x:'+Math.round(_previewDragOffsetX||0)+'  y:'+Math.round(_previewDragOffsetY||0);
    var illusOffsetStr = 'x:'+Math.round(_illusDragOffsetX||0)+'  y:'+Math.round(_illusDragOffsetY||0);
    var isPhotoArea = il && (il.classList.contains('prod-photo-preview-area') || il.classList.contains('prod-photo-preview'));

    var textLines = c.querySelectorAll('.prod-text-line'), textRowsHtml = '';
    textLines.forEach(function(tl, idx) {
      var sp = tl.querySelector('span'), tlR = tl.getBoundingClientRect();
      var off = _textOffsets[idx] || {x:0,y:0};
      var fs = sp ? (sp.style.fontSize || getComputedStyle(sp).fontSize) : '-';
      var fw = sp ? (sp.style.fontWeight || getComputedStyle(sp).fontWeight) : '-';
      var ff = sp ? (sp.style.fontFamily || getComputedStyle(sp).fontFamily) : '-';
      ff = ff ? ff.split(',')[0].replace(/['"]/g,'').trim() : '-';
      var label = (_config.text_lines && _config.text_lines[idx] && _config.text_lines[idx].label) ? _config.text_lines[idx].label : ('Line '+(idx+1));
      textRowsHtml += H('Text Line '+(idx+1), '— '+label)
        + D('Position', tlR ? 'x:'+r(tlR.left,ox)+'  y:'+r(tlR.top,oy) : '-', idx%2===0)
        + D('Size', tlR ? f(tlR.width)+' x '+f(tlR.height)+' px' : '-', idx%2!==0)
        + D('Drag offset', 'x:'+Math.round(off.x)+'  y:'+Math.round(off.y), idx%2===0)
        + D('Font', ff, idx%2!==0)
        + D('Size / Weight', fs+' / '+fw, idx%2===0);
    });
    body.innerHTML = '<table style="width:100%;border-collapse:collapse">'
      + H('Canvas','INFO')
      + D('CSS size', f(cR.width)+' x '+f(cR.height)+' px')
      + D('Ratio', cR.width&&cR.height ? (cR.width/cR.height).toFixed(2)+' : 1' : '-', true)
      + D('Classes', canvasClasses, false)
      + H('Photo','IMAGE')
      + D('Container pos', iR ? 'x:'+r(iR.left,ox)+'  y:'+r(iR.top,oy) : '-')
      + D('Container size', iR ? f(iR.width)+' x '+f(iR.height)+' px' : '-', true)
      + D('Slider size', photoSizePct, false)
      + D('Transform scale', photoScale, true)
      + D('Drag offset', isPhotoArea ? dragOffsetStr : illusOffsetStr, false)
      + (isCurved ? D('Curved zoom', (_curvedPhotoScale||1).toFixed(3), true) : '')
      + parHtml
      + textRowsHtml + '</table>';
  }

  // ══════════════════════════════════════════════════════════
  // ★ v4.9: REVIEW 按钮 — 生成成品图预览 + 获取 URL 链接
  // 手机端弹窗展示效果图；桌面端同时显示 URL 可复制
  // ══════════════════════════════════════════════════════════

  function _injectReviewBtn() {
    if (document.getElementById('prodReviewBtn')) return;

    // 找 submit 按钮的父容器，在它上方插入 Review 按钮
    const submitBtn = dom('prodSubmitBtn');
    if (!submitBtn) return;
    const submitParent = submitBtn.closest('.prod-submit-row, .prod-form-actions, .prod-btn-row, form') || submitBtn.parentElement;
    if (!submitParent) return;

    // 注入样式（只注入一次）
    if (!document.getElementById('_reviewBtnStyle')) {
      const st = document.createElement('style');
      st.id = '_reviewBtnStyle';
      st.textContent = `
        #prodReviewBtn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          width: 100%; padding: 13px 20px; margin-bottom: 10px;
          background: linear-gradient(135deg, #f0ebff 0%, #e8dfff 100%);
          color: #6c3fc5; border: 2px solid #c4b0f0; border-radius: 50px;
          font-size: 15px; font-weight: 700; cursor: pointer;
          letter-spacing: 0.3px; transition: all 0.2s ease;
          font-family: inherit;
        }
        #prodReviewBtn:hover { background: linear-gradient(135deg, #e8dfff, #d9ccff); border-color: #a98ee8; }
        #prodReviewBtn:disabled { opacity: 0.6; cursor: not-allowed; }
        #prodReviewBtn .rv-spinner {
          width: 16px; height: 16px; border: 2px solid #c4b0f0;
          border-top-color: #6c3fc5; border-radius: 50%;
          animation: _rvSpin 0.7s linear infinite; display: none;
        }
        @keyframes _rvSpin { to { transform: rotate(360deg); } }

        /* ── 弹窗 ── */
        #_reviewModal {
          position: fixed; inset: 0; background: rgba(0,0,0,0.6);
          z-index: 999999; display: flex; align-items: center; justify-content: center;
          animation: _rvFadeIn 0.2s ease; padding: 16px; box-sizing: border-box;
        }
        @keyframes _rvFadeIn { from { opacity: 0; } to { opacity: 1; } }
        #_reviewModal .rv-box {
          background: #fff; border-radius: 20px;
          width: 100%; max-width: 420px; max-height: 90vh;
          overflow-y: auto; box-shadow: 0 24px 64px rgba(0,0,0,0.35);
          animation: _rvPop 0.25s cubic-bezier(0.34,1.56,0.64,1);
        }
        @keyframes _rvPop { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        #_reviewModal .rv-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 18px 20px 14px; border-bottom: 1px solid #f0ebff;
        }
        #_reviewModal .rv-title {
          font-size: 17px; font-weight: 800; color: #3d2270; letter-spacing: 0.3px;
        }
        #_reviewModal .rv-close {
          width: 32px; height: 32px; border-radius: 50%; border: none;
          background: #f4f0ff; color: #9b7fd4; font-size: 18px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; transition: background 0.15s;
        }
        #_reviewModal .rv-close:hover { background: #e8dfff; }
        #_reviewModal .rv-img-wrap {
          padding: 20px 20px 0; display: flex; justify-content: center;
        }
        #_reviewModal .rv-img {
          max-width: 100%; max-height: 340px; border-radius: 12px;
          box-shadow: 0 8px 32px rgba(108,63,197,0.18); display: block;
        }
        #_reviewModal .rv-url-section {
          padding: 16px 20px 0; margin-top: 4px;
        }
        #_reviewModal .rv-url-label {
          font-size: 11px; font-weight: 700; color: #9b7fd4;
          letter-spacing: 1px; text-transform: uppercase; margin-bottom: 6px;
        }
        #_reviewModal .rv-url-row {
          display: flex; align-items: center; gap: 8px;
        }
        #_reviewModal .rv-url-input {
          flex: 1; padding: 9px 11px; border-radius: 10px;
          border: 1.5px solid #e0d5f0; font-size: 11.5px; color: #555;
          background: #faf8ff; outline: none; min-width: 0;
          font-family: monospace; overflow: hidden; text-overflow: ellipsis;
          white-space: nowrap;
        }
        #_reviewModal .rv-copy-btn {
          flex-shrink: 0; padding: 9px 14px; border-radius: 10px;
          border: none; background: #6c3fc5; color: #fff;
          font-size: 12px; font-weight: 700; cursor: pointer;
          transition: background 0.15s; white-space: nowrap;
        }
        #_reviewModal .rv-copy-btn:hover { background: #5a32a3; }
        #_reviewModal .rv-copy-btn.copied { background: #2e9e5b; }
        #_reviewModal .rv-actions {
          padding: 16px 20px 20px; display: flex; gap: 10px;
        }
        #_reviewModal .rv-dl-btn {
          flex: 1; padding: 12px; border-radius: 50px;
          border: 2px solid #e0d5f0; background: #fff; color: #6c3fc5;
          font-size: 14px; font-weight: 700; cursor: pointer;
          transition: all 0.15s;
        }
        #_reviewModal .rv-dl-btn:hover { background: #f4f0ff; border-color: #c4b0f0; }
        #_reviewModal .rv-processing {
          padding: 48px 20px; text-align: center;
        }
        #_reviewModal .rv-proc-spinner {
          width: 44px; height: 44px; border: 4px solid #e0d5f0;
          border-top-color: #6c3fc5; border-radius: 50%;
          animation: _rvSpin 0.8s linear infinite; margin: 0 auto 16px;
        }
        #_reviewModal .rv-proc-text {
          font-size: 14px; color: #9b7fd4; font-weight: 600;
        }
      `;
      document.head.appendChild(st);
    }

    // 创建按钮
    const btn = document.createElement('button');
    btn.id = 'prodReviewBtn';
    btn.type = 'button';
    btn.innerHTML = '<span class="rv-spinner"></span><span class="rv-label">🔍 REVIEW & DOWNLOAD PREVIEW</span>';
    submitParent.insertBefore(btn, submitBtn);

    btn.addEventListener('click', _openReviewModal);
  }

  async function _openReviewModal() {
    const btn = dom('prodReviewBtn');
    const spinner  = btn?.querySelector('.rv-spinner');
    const label    = btn?.querySelector('.rv-label');

    // 设置按钮 loading 状态
    if (btn)     { btn.disabled = true; }
    if (spinner) { spinner.style.display = 'block'; }
    if (label)   { label.textContent = 'Generating...'; }

    // 移除旧弹窗
    document.getElementById('_reviewModal')?.remove();

    // 先显示带 loading 的弹窗（让用户知道在处理）
    const modal = document.createElement('div');
    modal.id = '_reviewModal';
    modal.innerHTML = `
      <div class="rv-box">
        <div class="rv-header">
          <span class="rv-title">✦ Stamp Preview</span>
          <button class="rv-close" id="_reviewClose">✕</button>
        </div>
        <div class="rv-processing" id="_reviewProcessing">
          <div class="rv-proc-spinner"></div>
          <div class="rv-proc-text">Generating your stamp preview…</div>
        </div>
      </div>`;
    document.body.appendChild(modal);

    // 关闭逻辑
    const _closeModal = () => {
      modal.style.transition = 'opacity 0.15s';
      modal.style.opacity = '0';
      setTimeout(() => modal.remove(), 160);
    };
    document.getElementById('_reviewClose').addEventListener('click', _closeModal);
    modal.addEventListener('click', e => { if (e.target === modal) _closeModal(); });
    document.addEventListener('keydown', function _escClose(e) {
      if (e.key === 'Escape') { _closeModal(); document.removeEventListener('keydown', _escClose); }
    });

    try {
      // 1. 生成成品图
      const tc = {};
      _config.text_lines?.forEach((l, i) => {
        const inp = dom(`prodTextLine${i}`);
        if (inp) tc[l.label] = inp.value.trim();
      });
      const imgData = await _buildPreviewImage(tc);
      if (!imgData) throw new Error('Preview generation failed');

      // 2. 上传到 Cloudinary 获取 URL
      let previewUrl = '';
      try {
        previewUrl = await Core.uploadToCloudinary(imgData, _creds.cloudinaryCloudName, _creds.cloudinaryPreset);
      } catch (uploadErr) {
        console.warn('[Review] Cloudinary upload failed, showing local preview only:', uploadErr);
      }

      // 3. 更新弹窗内容
      const processing = document.getElementById('_reviewProcessing');
      if (!processing) return; // 弹窗已被关闭

      const isMobile = window.innerWidth <= 768;
      const urlSection = ''; // ★ URL hidden from customers

      const dlSection = `
        <div class="rv-actions">
          <button class="rv-dl-btn" id="_reviewDlBtn">⬇ Download Image</button>
        </div>`;

      processing.outerHTML = `
        <div class="rv-img-wrap">
          <img class="rv-img" id="_reviewImg" src="${imgData}" alt="Stamp Preview" />
        </div>
        ${urlSection}
        ${dlSection}`;

      // 4. 复制 URL
      const copyBtn = document.getElementById('_reviewCopyBtn');
      const urlInput = document.getElementById('_reviewUrlInput');
      if (copyBtn && urlInput) {
        copyBtn.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(urlInput.value);
          } catch {
            urlInput.select();
            document.execCommand('copy');
          }
          copyBtn.textContent = '✓ Copied!';
          copyBtn.classList.add('copied');
          setTimeout(() => { copyBtn.textContent = 'Copy'; copyBtn.classList.remove('copied'); }, 2000);
        });
      }

      // 5. 下载图片 — 邮箱验证 + 低像素水印
      const dlBtn = document.getElementById('_reviewDlBtn');
      if (dlBtn) {
        dlBtn.addEventListener('click', () => _openWatermarkDownloadModal(imgData));
      }

    } catch (err) {
      console.error('[Review] Failed:', err);
      const processing = document.getElementById('_reviewProcessing');
      if (processing) {
        processing.innerHTML = `
          <div style="font-size:40px;margin-bottom:12px;">⚠️</div>
          <div style="font-size:14px;color:#e53e3e;font-weight:600;">Failed to generate preview.<br>Please try again.</div>
          <button onclick="document.getElementById('_reviewModal')?.remove()" style="margin-top:20px;padding:10px 28px;border-radius:50px;border:none;background:#6c3fc5;color:#fff;font-size:14px;font-weight:700;cursor:pointer;">Close</button>`;
      }
    } finally {
      // 恢复按钮状态
      if (btn)     { btn.disabled = false; }
      if (spinner) { spinner.style.display = 'none'; }
      if (label)   { label.textContent = '🔍 REVIEW & DOWNLOAD PREVIEW'; }
    }
  }

  function _openWatermarkDownloadModal(srcDataUrl) {
    document.getElementById('_wmDlOverlay')?.remove();
    const overlay = document.createElement('div');
    overlay.id = '_wmDlOverlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:9999999;display:flex;align-items:center;justify-content:center;';
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:14px;padding:28px 24px;width:320px;box-shadow:0 8px 32px rgba(0,0,0,0.22);font-family:inherit;">
        <div style="font-weight:700;font-size:17px;margin-bottom:4px;">Download Preview</div>
        <div style="font-size:12px;color:#999;margin-bottom:18px;line-height:1.6;">
          Enter your email to download a low-res preview.<br>
          <span style="color:#b090d8;">Full quality included with your order.</span>
        </div>
        <input id="_wmEmailInput" type="email" placeholder="your@email.com"
          style="width:100%;box-sizing:border-box;padding:10px 12px;border:1.5px solid #d0c8c0;border-radius:8px;font-size:14px;outline:none;margin-bottom:8px;">
        <div id="_wmEmailErr" style="color:#e53e3e;font-size:12px;min-height:16px;margin-bottom:12px;"></div>
        <div style="display:flex;gap:10px;">
          <button id="_wmCancelBtn" type="button"
            style="flex:1;padding:10px;border:1.5px solid #d0c8c0;border-radius:8px;background:#fff;font-size:13px;cursor:pointer;">Cancel</button>
          <button id="_wmConfirmBtn" type="button"
            style="flex:1;padding:10px;border:none;border-radius:8px;background:#6c3fc5;color:#fff;font-size:13px;font-weight:700;cursor:pointer;">Download</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const emailInput = overlay.querySelector('#_wmEmailInput');
    const errMsg     = overlay.querySelector('#_wmEmailErr');
    const confirmBtn = overlay.querySelector('#_wmConfirmBtn');
    const cancelBtn  = overlay.querySelector('#_wmCancelBtn');
    setTimeout(() => emailInput.focus(), 50);

    function buildWatermarked(src) {
      return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
          const LW = 500, LH = Math.round(img.height * 500 / img.width);
          const c  = document.createElement('canvas');
          c.width = LW; c.height = LH;
          const cx = c.getContext('2d');
          cx.drawImage(img, 0, 0, LW, LH);
          const wt = 'PLANTANDPRESS.COM';
          cx.save();
          cx.font      = 'bold 16px Arial,sans-serif';
          cx.fillStyle = 'rgba(90,40,160,0.28)';
          cx.textAlign = 'center';
          for (let wy = 40; wy < LH + 40; wy += 220) {
            for (let wx = 0; wx < LW + 100; wx += 380) {
              cx.save();
              cx.translate(wx, wy);
              cx.rotate(-28 * Math.PI / 180);
              cx.fillText(wt, 0, 0);
              cx.restore();
            }
          }
          cx.restore();
          resolve(c.toDataURL('image/jpeg', 0.65));
        };
        img.src = src;
      });
    }

    const tryDownload = async () => {
      const email = emailInput.value.trim();
      if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        errMsg.textContent = 'Please enter a valid email address.';
        emailInput.style.borderColor = '#e53e3e';
        emailInput.focus();
        return;
      }
      confirmBtn.textContent = 'Processing...';
      confirmBtn.disabled = true;
      overlay.remove();

      // 1. 客人下载低清水印图
      const watermarked = await buildWatermarked(srcDataUrl);
      const a = document.createElement('a');
      a.href = watermarked;
      a.download = 'stamp-preview.jpg';
      a.style.display = 'none';
      document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 200);

      // 2. 上传高清原图 → 发邮件给卖家（带客人邮箱 + 高清URL）
      try {
        const hdUrl = await Core.uploadToCloudinary(
          srcDataUrl, _creds.cloudinaryCloudName, _creds.cloudinaryPreset
        );
        if (typeof emailjs !== 'undefined' && _creds.sellerEmail) {
          emailjs.init(_creds.emailjsPublicKey);
          emailjs.send(_creds.emailjsServiceId, _creds.emailjsTemplateId, {
            to_email:       _creds.sellerEmail,
            customer_email: email,
            preview_image:  hdUrl,
            preview_url:    hdUrl,
            configuration:  'Preview Download Request',
            text_content:   `Customer downloaded a preview image.\n\nCustomer email: ${email}\n\nPreview image URL:\n${hdUrl}`,
            ink_color:      'N/A',
            stamp_size:     'N/A',
            border_style:   'N/A',
            order_date:     new Date().toLocaleString()
          }).catch(err => console.warn('[WatermarkDl] Seller email skipped:', err));
        }
      } catch(uploadErr) {
        console.warn('[WatermarkDl] Upload/email skipped:', uploadErr);
      }
    };

    confirmBtn.addEventListener('click', tryDownload);
    emailInput.addEventListener('keydown', e => { if (e.key === 'Enter') tryDownload(); });
    cancelBtn.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
  }

  return { init, handleFormSubmit, buildPreviewImage: _buildPreviewImage };
})();